import { formatApiErrorDetail } from "@/lib/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, CheckCircle, Camera, DownloadSimple, Microphone, CalendarBlank, Package, FilePdf } from "@phosphor-icons/react";
import { AdminShell } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n";
import VoiceNoteButton from "@/components/VoiceNoteSpanish";
import AIAssessModal from "@/components/AIAssessModal";
import WalkMode from "@/components/WalkMode";
import VoiceToPunchModal from "@/components/VoiceToPunchModal";
import AutoDeployCamModal from "@/components/AutoDeployCamModal";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token") || ""}` } });
// Single source of truth for the JWT — same fallback the `auth()` helper uses.
const jwt = () => localStorage.getItem("access_token") || localStorage.getItem("token") || "";

const CATS = ["paint","plumbing","electrical","flooring","drywall","fixtures","cleaning","appliance","exterior","landscaping","hvac","safety","other"];

function computeKpis(items) {
  const k = { total: (items || []).length, open: 0, in_progress: 0, deferred: 0, complete: 0, cost_cents: 0 };
  (items || []).forEach(it => {
    if (k[it.status] !== undefined) k[it.status] += 1;
    k.cost_cents += parseInt(it.cost_cents || 0);
  });
  return k;
}
const CAT_COLOR = {
  paint: "#c026d3", plumbing: "#0891b2", electrical: "#eab308", flooring: "#a16207",
  drywall: "#78716c", fixtures: "#0f172a", cleaning: "#14b8a6", appliance: "#4f46e5",
  exterior: "#059669", landscaping: "#65a30d", hvac: "#f97316", safety: "#dc2626", other: "#475569",
};
const STATUSES = ["open","in_progress","deferred","complete"];
const STATUS_LABEL = { open: "Open", in_progress: "In Prog", deferred: "Deferred", complete: "Complete" };
const STATUS_LABEL_KEY = { open: "pr.status.open", in_progress: "pr.status.in_progress", deferred: "pr.status.deferred", complete: "pr.status.complete" };
const STATUS_BG = { open: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", deferred: "bg-slate-200 text-slate-700", complete: "bg-emerald-100 text-emerald-800" };
const PRIORITIES = ["low","med","high","urgent"];
const PRIORITY_BG = { low: "bg-slate-100 text-slate-600", med: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700 font-black", urgent: "bg-pink-500 text-white font-black animate-pulse" };

function AddItemInline({ projectId, rooms, defaultRoom, onAdded }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", room: defaultRoom || rooms[0] || "", category: "other", priority: "med", assignee: "", cost_cents: 0, description: "", project_only: false, min_photos: 0, discovery: "walkthrough", needs_approval: false });
  const [busy, setBusy] = useState(false);
  const autoProjectOnly = (form.room || "").toLowerCase() === "all units";
  const effectiveProjectOnly = autoProjectOnly || form.project_only;
  const isNewFind = form.discovery === "post";
  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("pr.toast.title_required")); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/projects/${projectId}/punch`, { ...form, project_only: effectiveProjectOnly }, auth());
      onAdded(data.item);
      setForm({ title: "", room: defaultRoom || rooms[0] || "", category: "other", priority: "med", assignee: "", cost_cents: 0, description: "", project_only: false, min_photos: 0, discovery: "walkthrough", needs_approval: false });
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? t("pr.toast.couldnt_add"))); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="punch-add-open"
      className="w-full p-3 bg-white border-2 border-dashed border-slate-300 hover:border-fuchsia-600 hover:bg-fuchsia-50 font-black uppercase text-xs tracking-widest text-slate-600 hover:text-fuchsia-700 flex items-center justify-center gap-2">
      <Plus weight="bold" size={14}/> Add Punch Item
    </button>
  );
  return (
    <div className="p-3 bg-white border-2 border-fuchsia-600 space-y-2" data-testid="punch-add-form">
      <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="punch-title"
        placeholder="What needs fixing? (e.g. Patch drywall behind bathroom door)" className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.room} onChange={e => setForm({...form, room: e.target.value})} data-testid="punch-room" className="h-11 px-2 border-2 border-slate-300 text-sm bg-white">
          <option value="">— Room —</option>
          {rooms.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="punch-category" className="h-11 px-2 border-2 border-slate-300 text-sm bg-white">
          {CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} data-testid="punch-priority" className="h-11 px-2 border-2 border-slate-300 text-sm bg-white">
          {PRIORITIES.map(p => <option key={p} value={p}>{p} priority</option>)}
        </select>
        <input value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})} data-testid="punch-assignee"
          placeholder="Assignee (opt)" className="h-11 px-3 border-2 border-slate-300 text-sm"/>
        <input value={form.cost_cents ? (form.cost_cents/100).toFixed(2) : ""} type="number" step="0.01"
          onChange={e => setForm({...form, cost_cents: Math.round(parseFloat(e.target.value || 0) * 100)})}
          data-testid="punch-cost" placeholder="Est cost $ (opt)" className="h-11 px-3 border-2 border-slate-300 text-sm"/>
        <input value={form.min_photos} type="number" min="0" max="20"
          onChange={e => setForm({...form, min_photos: parseInt(e.target.value || 0)})}
          data-testid="punch-min-photos" placeholder="📸 Required photos" title="Photos required before marking complete"
          className="h-11 px-3 border-2 border-slate-300 text-sm"/>
      </div>
      <textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="punch-description"
        placeholder="Notes (optional)" className="w-full px-3 py-2 border-2 border-slate-300 text-sm resize-none"/>
      <label className={`flex items-center gap-2 p-2 border-2 text-xs cursor-pointer ${effectiveProjectOnly ? "bg-violet-50 border-violet-600 text-violet-800" : "bg-white border-slate-200 text-slate-600"}`}>
        <input type="checkbox" checked={effectiveProjectOnly} disabled={autoProjectOnly}
          onChange={e => setForm({...form, project_only: e.target.checked})}
          data-testid="punch-project-only" className="h-4 w-4"/>
        <span className="font-black uppercase tracking-widest text-[10px]">
          🔒 Project-only {autoProjectOnly ? "(auto-set for All Units)" : "— skip Maintenance dispatch"}
        </span>
      </label>
      <label className={`flex items-center gap-2 p-2 border-2 text-xs cursor-pointer ${isNewFind ? "bg-pink-50 border-pink-500 text-pink-800" : "bg-white border-slate-200 text-slate-600"}`}>
        <input type="checkbox" checked={isNewFind}
          onChange={e => setForm({...form, discovery: e.target.checked ? "post" : "walkthrough", needs_approval: e.target.checked ? form.needs_approval : false})}
          data-testid="punch-new-find" className="h-4 w-4"/>
        <span className="font-black uppercase tracking-widest text-[10px]">
          🆕 Discovered post-walkthrough (new find)
        </span>
      </label>
      {isNewFind && (
        <label className={`flex items-center gap-2 p-2 border-2 text-xs cursor-pointer ${form.needs_approval ? "bg-orange-50 border-orange-500 text-orange-800" : "bg-white border-slate-200 text-slate-600"}`}>
          <input type="checkbox" checked={form.needs_approval}
            onChange={e => setForm({...form, needs_approval: e.target.checked})}
            data-testid="punch-needs-approval" className="h-4 w-4"/>
          <span className="font-black uppercase tracking-widest text-[10px]">
            📝 Needs Leslie's approval (extra scope)
          </span>
        </label>
      )}
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="flex-1 h-10 border-2 border-slate-300 font-black uppercase text-xs tracking-widest">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="punch-add-submit"
          className="flex-1 h-10 bg-emerald-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
          {busy ? "Adding…" : "Add"}
        </button>
      </div>
    </div>
  );
}

function PunchRow({ projectId, item, onChanged, onRemoved, bulkMode, selected, onToggleSelect }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const photoCount = (item.photos || []).length;
  const minPhotos = parseInt(item.min_photos || 0);
  const photoGap = Math.max(0, minPhotos - photoCount);
  const patch = async (upd, force = false) => {
    setBusy(true);
    try {
      const url = `${BASE}/api/projects/${projectId}/punch/${item.id}${force ? "?force=true" : ""}`;
      await axios.patch(url, upd, auth());
      onChanged({ ...item, ...upd });
    } catch (e) {
      const msg = e.response?.data?.detail || t("pr.toast.couldnt_update");
      if (e.response?.status === 400 && msg.includes("photo")) {
        if (window.confirm(`${msg}\n\nOverride and mark complete anyway?`)) {
          patch(upd, true);
          return;
        }
      } else {
        toast.error(msg);
      }
    }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(t("pr.confirm.delete_punch"))) return;
    try {
      await axios.delete(`${BASE}/api/projects/${projectId}/punch/${item.id}`, auth());
      onRemoved(item.id);
    } catch { toast.error("Couldn't delete"); }
  };
  const uploadPhoto = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      try {
        await axios.post(`${BASE}/api/projects/${projectId}/punch/${item.id}/photo`, fd, {
          headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch { fail += 1; }
    }
    e.target.value = "";
    if (ok) toast.success(ok === 1 ? t("pr.toast.photo_added") : `${ok} photos added`);
    if (fail) toast.error(`${fail} failed`);
    if (ok) onChanged({ ...item, photos: [...(item.photos || []), ...Array(ok).fill("new")] });
  };

  const catColor = CAT_COLOR[item.category] || "#475569";

  return (
    <div className={`p-3 bg-white border-2 ${selected ? "border-emerald-500 bg-emerald-50" : "border-[#0f172a]"} relative`} data-testid={`punch-${item.id}`}
      style={{ borderLeftWidth: "6px", borderLeftColor: selected ? "#10b981" : catColor }}>
      <div className="flex items-start gap-2">
        {bulkMode && (
          <label className="mt-1 cursor-pointer">
            <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect(item.id)}
              data-testid={`punch-select-${item.id}`} className="h-5 w-5 accent-emerald-600"/>
          </label>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap mb-1">
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_BG[item.status] || STATUS_BG.open}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${PRIORITY_BG[item.priority] || PRIORITY_BG.med}`}>
              {item.priority}
            </span>
            {item.room && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">{item.room}</span>}
            <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: catColor }}>{item.category}</span>
            {item.discovery === "post" && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-pink-500 text-white">{t("pr.badge.new_find")}</span>
            )}
            {item.needs_approval && item.approval_status === "pending" && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-orange-500 text-white animate-pulse">{t("pr.badge.pending_approval")}</span>
            )}
            {item.approval_status === "approved" && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white">{t("pr.badge.approved")}</span>
            )}
            {item.approval_status === "rejected" && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">{t("pr.badge.rejected")}</span>
            )}
            {item.verified_by_owner && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-teal-600 text-white" title={t("pr.tt.verified_by", { who: item.verified_by || "PM" })}>{t("pr.badge.pm_verified")}</span>
            )}
            {item.project_only && (
              <button onClick={() => patch({ project_only: false })} disabled={busy} data-testid={`punch-project-only-badge-${item.id}`}
                title={t("pr.tt.enable_dispatch")}
                className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-violet-600 text-white hover:bg-violet-700">
                {t("pr.badge.project_only")}
              </button>
            )}
            {!item.project_only && (
              <button onClick={() => patch({ project_only: true })} disabled={busy} data-testid={`punch-mark-project-only-${item.id}`}
                title={t("pr.tt.mark_project_only")}
                className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-white border border-violet-300 text-violet-500 hover:bg-violet-50">
                {t("pr.badge.mark_project_only")}
              </button>
            )}
          </div>
          <button onClick={() => setExpanded(v => !v)} className="text-left w-full group" data-testid={`punch-title-${item.id}`}>
            <div className="font-black text-sm flex items-center gap-1 group-hover:text-cyan-700 transition-colors">
              <span className="text-slate-400 group-hover:text-cyan-600 transition-transform" style={{transform: expanded ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block"}}>▸</span>
              <span className="flex-1">{item.title}</span>
            </div>
          </button>
          {item.description && <div className="text-xs text-slate-600 italic mt-0.5">{item.description}</div>}
          <div className="flex items-center gap-3 text-[10px] mt-1">
            {item.assignee && <span className="text-slate-500">👤 {item.assignee}</span>}
            {item.quote_status === "needed" ? (
              <span className="px-1.5 py-0.5 font-black uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-500">{t("pr.badge.quote_needed")}</span>
            ) : (item.cost_cents !== undefined && !!item.cost_cents && <span className="text-slate-500">💵 ${(item.cost_cents/100).toFixed(2)}</span>)}
            {minPhotos > 0 && (
              <span data-testid={`punch-photo-gate-${item.id}`}
                className={`px-1.5 py-0.5 font-black uppercase tracking-widest ${photoGap === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                {photoGap > 0
                  ? t("pr.badge.photos_need_more", { count: photoCount, min: minPhotos, gap: photoGap })
                  : t("pr.badge.photos_ok", { count: photoCount, min: minPhotos })}
              </span>
            )}
            {minPhotos === 0 && photoCount > 0 && <span className="text-slate-500">📸 {photoCount}</span>}
          </div>
          {expanded && (
            <div className="mt-2 p-2 bg-slate-50 border border-slate-200 space-y-2" data-testid={`punch-detail-${item.id}`}>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                <div><span className="font-black uppercase tracking-widest text-slate-500 text-[9px]">{t("pr.detail.added")}</span> · {(item.added_at || "").slice(0, 10) || "—"}</div>
                <div><span className="font-black uppercase tracking-widest text-slate-500 text-[9px]">{t("pr.detail.updated")}</span> · {(item.updated_at || "").slice(0, 10) || "—"}</div>
                {item.completed_at && <div><span className="font-black uppercase tracking-widest text-emerald-600 text-[9px]">{t("pr.detail.completed")}</span> · {item.completed_at.slice(0, 10)}</div>}
                {item.approved_at && <div><span className="font-black uppercase tracking-widest text-emerald-600 text-[9px]">{t("pr.detail.approved_by", { who: item.approved_by || "landlord" })}</span> · {item.approved_at.slice(0, 10)}</div>}
                <div><span className="font-black uppercase tracking-widest text-slate-500 text-[9px]">{t("pr.detail.discovery")}</span> · {item.discovery || "walkthrough"}</div>
                <div><span className="font-black uppercase tracking-widest text-slate-500 text-[9px]">{t("pr.detail.quote")}</span> · {item.quote_status || "submitted"}</div>
              </div>
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Notes / Description</label>
                <textarea rows={7} defaultValue={item.description || ""}
                  onBlur={e => e.target.value !== (item.description || "") && patch({description: e.target.value})}
                  data-testid={`punch-description-edit-${item.id}`}
                  className="w-full px-2 py-1 border border-slate-300 text-xs font-mono resize-y"/>
                <div className="mt-2">
                  <VoiceNoteButton onNote={({ es, en }) => {
                      const next = (item.description ? item.description + "\n" : "") + (en || es);
                      patch({ description: next });
                    }}/>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">{t("pr.detail.assignee")}</label>
                <input defaultValue={item.assignee || ""} onBlur={e => e.target.value !== (item.assignee || "") && patch({assignee: e.target.value})}
                  placeholder={t("pr.detail.assignee_placeholder")}
                  data-testid={`punch-assignee-${item.id}`}
                  className="flex-1 h-7 px-2 border border-slate-300 text-xs"/>
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">{t("pr.detail.min_photos")}</label>
                <input type="number" min="0" max="20" defaultValue={item.min_photos || 0}
                  onBlur={e => parseInt(e.target.value) !== (item.min_photos || 0) && patch({min_photos: parseInt(e.target.value) || 0})}
                  data-testid={`punch-minphotos-${item.id}`}
                  className="w-16 h-7 px-2 border border-slate-300 text-xs"/>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-2">Cost $</label>
                {item.cost_cents !== undefined ? (
                  <input type="number" step="0.01" min="0" defaultValue={((item.cost_cents || 0)/100).toFixed(2)}
                    onBlur={e => Math.round(parseFloat(e.target.value)*100) !== item.cost_cents && patch({cost_cents: Math.round(parseFloat(e.target.value)*100) || 0})}
                    data-testid={`punch-cost-${item.id}`}
                    className="w-24 h-7 px-2 border border-slate-300 text-xs"/>
                ) : (
                  <span className="text-[10px] italic text-slate-400">hidden</span>
                )}
              </div>
              {photoCount > 0 && (
                <div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-2">
                    <span>Photos ({photoCount})</span>
                    {(() => {
                      const visits = new Set((item.photos || []).filter(p => typeof p === "object" && p.visit_number).map(p => p.visit_number));
                      return visits.size > 1 && <span className="px-1 py-0.5 bg-cyan-100 text-cyan-800 rounded normal-case tracking-normal">{visits.size} visits</span>;
                    })()}
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-1">
                    {(item.photos || []).filter(p => p !== "new").map((raw, i) => {
                      const obj = typeof raw === "string" ? { path: raw } : raw;
                      if (!obj.path) return null;
                      const src = `${BASE}/api/projects/${projectId}/punch-photo?path=${encodeURIComponent(obj.path)}&auth=${encodeURIComponent(jwt())}`;
                      const stamp = obj.at ? new Date(obj.at).toLocaleDateString(undefined, {month:"short", day:"numeric"}) : "";
                      const visit = obj.visit_number || 1;
                      return (
                        <a key={i} href={src} target="_blank" rel="noreferrer"
                          title={`${obj.prompt || item.title || ""}${obj.unit ? " · Unit " + obj.unit : ""}${stamp ? " · " + stamp : ""}${obj.by ? " · " + obj.by : ""}`}
                          data-testid={`punch-photo-thumb-${item.id}-${i}`}
                          className="relative block group">
                          <img src={src} alt="" className="w-full aspect-square object-cover border border-slate-300 hover:border-cyan-500"/>
                          {stamp && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[8px] font-black uppercase tracking-widest px-1 py-0.5 text-center">
                              V{visit}·{stamp}
                            </div>
                          )}
                        </a>
                      );
                    })}
                  </div>
                  {item.status === "complete" && (
                    <label className="mt-2 inline-flex items-center gap-1 h-7 px-2 border-2 border-cyan-500 bg-cyan-50 hover:bg-cyan-100 text-cyan-800 font-black uppercase text-[9px] tracking-widest cursor-pointer"
                      data-testid={`punch-rewalk-${item.id}`}>
                      🔁 Re-walk · Add More Photos
                      <input type="file" accept="image/*" capture="environment" multiple onChange={uploadPhoto} className="hidden"/>
                    </label>
                  )}
                </div>
              )}
              {item.approver_note && (
                <div className="text-xs italic text-slate-600 border-l-2 border-orange-400 pl-2">
                  <span className="font-black uppercase tracking-widest text-orange-600 text-[9px]">Note:</span> "{item.approver_note}"
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1" style={{ display: bulkMode ? "none" : "flex" }}>
          {item.status !== "complete" ? (
            <button onClick={() => patch({ status: "complete" })} disabled={busy} data-testid={`punch-complete-${item.id}`}
              className="h-8 w-8 bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center disabled:opacity-40" title={t("pr.tt.mark_complete")}>
              <CheckCircle weight="bold" size={16}/>
            </button>
          ) : (
            <button onClick={() => patch({ status: "open" })} disabled={busy} data-testid={`punch-reopen-${item.id}`}
              className="h-8 w-8 bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center disabled:opacity-40" title={t("pr.tt.reopen")}>
              ↺
            </button>
          )}
          <label className="h-8 w-8 bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center cursor-pointer" title={t("pr.tt.photo_camera")}>
            <Camera weight="bold" size={14}/>
            <input type="file" accept="image/*" capture="environment" multiple onChange={uploadPhoto} className="hidden" data-testid={`punch-photo-${item.id}`}/>
          </label>
          <label className="h-8 w-8 bg-slate-600 hover:bg-slate-500 text-white flex items-center justify-center cursor-pointer" title={t("pr.tt.photo_gallery")}>
            <span className="text-xs font-black">🖼</span>
            <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" data-testid={`punch-photo-gallery-${item.id}`}/>
          </label>
          <button onClick={del} data-testid={`punch-delete-${item.id}`} className="h-8 w-8 bg-red-600 hover:bg-red-700 text-white flex items-center justify-center" title={t("pr.tt.delete")}>
            <Trash weight="bold" size={14}/>
          </button>
        </div>
      </div>
      <div className="mt-2 flex gap-1 flex-wrap" style={{ display: bulkMode ? "none" : "flex" }}>
        {STATUSES.filter(s => s !== item.status).map(s => (
          <button key={s} onClick={() => patch({ status: s })} disabled={busy} data-testid={`punch-status-${item.id}-${s}`}
            className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 border-slate-300 hover:bg-slate-100 disabled:opacity-40`}>
            {t("pr.transition_prefix", { label: t(STATUS_LABEL_KEY[s]) })}
          </button>
        ))}
        {item.status === "complete" && (
          <label className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 cursor-pointer inline-flex items-center gap-1 ${item.verified_by_owner ? "bg-teal-600 border-teal-700 text-white" : "bg-white border-teal-500 text-teal-700 hover:bg-teal-50"}`}
            data-testid={`punch-verify-label-${item.id}`}>
            <input type="checkbox" checked={!!item.verified_by_owner}
              onChange={e => patch({ verified_by_owner: e.target.checked })}
              data-testid={`punch-verify-${item.id}`} className="h-3 w-3"/>
            {item.verified_by_owner ? "✓ I verified" : "I verified this"}
          </label>
        )}
        {item.needs_approval && item.approval_status === "pending" && (
          <>
            <button onClick={() => patch({ approval_status: "approved" })} disabled={busy}
              data-testid={`punch-approve-${item.id}`}
              className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700">
              ✓ Approve
            </button>
            <button onClick={() => patch({ approval_status: "rejected" })} disabled={busy}
              data-testid={`punch-reject-${item.id}`}
              className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 bg-red-600 border-red-700 text-white hover:bg-red-700">
              ✗ Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function UnitCodePill({ unit, buildingCode, unitCode, unitKeyCode, unitNote, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(unitCode || "");
  useEffect(() => { setDraft(unitCode || ""); }, [unitCode]);
  const title = [
    buildingCode ? `Building: ${buildingCode}` : "",
    unitKeyCode ? `Key ID: ${unitKeyCode}` : "",
    unitNote ? `⚠ ${unitNote}` : "",
  ].filter(Boolean).join(" · ");
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} data-testid={`unit-code-pill-${unit}`}
        className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1 ${
          unitCode ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-white hover:bg-slate-50 border-dashed text-slate-600"
        }`}
        title={title || "No codes yet"}>
        🔑 {unitCode ? unitCode : "Add code"}
        {unitKeyCode && <span className="ml-1 opacity-80 text-[9px]">· key {unitKeyCode}</span>}
        {unitNote && <span className="ml-1 text-red-100">⚠</span>}
      </button>
    );
  }
  return (
    <div className="h-9 flex items-center gap-1 border-2 border-amber-500 bg-amber-50 pl-2 pr-1" data-testid={`unit-code-edit-${unit}`}>
      <span className="text-[9px] font-black uppercase tracking-widest text-amber-700">Unit {unit}</span>
      <input value={draft} onChange={e => setDraft(e.target.value)} autoFocus
        placeholder="push-btn code"
        data-testid={`unit-code-input-${unit}`}
        className="h-7 w-28 px-2 border border-amber-500 text-sm font-mono"/>
      <button onClick={() => { onSave(draft); setEditing(false); }} data-testid={`unit-code-save-${unit}`}
        className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[9px] tracking-widest">Save</button>
      <button onClick={() => { setDraft(unitCode || ""); setEditing(false); }}
        className="h-7 px-2 text-slate-500 hover:text-black font-black text-xs">✕</button>
    </div>
  );
}

function ImportTurnKitButton({ projectId, defaultRoom, onImported }) {
  const [open, setOpen] = useState(false);
  const [kits, setKits] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${BASE}/api/turn-kits`, auth()).then(r => setKits(r.data.turn_kits || r.data || [])).catch(() => setKits([]));
  }, [open]);
  const submit = async () => {
    if (!selected) { toast.error("Pick a kit"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/projects/${projectId}/import-turn-kit`,
        { turn_kit_id: selected, default_room: defaultRoom || "" }, auth());
      toast.success(`Imported ${data.imported} items`);
      setOpen(false);
      onImported();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="project-import-kit-btn"
      className="h-9 px-3 bg-white border-2 border-[#0f172a] hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest">
      + Import from Turn Kit
    </button>
  );
  return (
    <div className="p-3 bg-white border-2 border-fuchsia-600 space-y-2" data-testid="project-import-kit-form">
      <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600">Import Turn Kit</div>
      <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="import-kit-select" className="w-full h-10 px-2 border-2 border-slate-300 text-sm bg-white">
        <option value="">Pick a kit…</option>
        {kits.map(k => <option key={k.id} value={k.id}>{k.name} ({(k.items || []).length} items)</option>)}
      </select>
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="flex-1 h-9 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest">Cancel</button>
        <button onClick={submit} disabled={busy || !selected} data-testid="import-kit-submit"
          className="flex-1 h-9 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
          {busy ? "Importing…" : "Import"}
        </button>
      </div>
    </div>
  );
}

function roomGroup(room) {
  if (!room) return "other";
  const r = room.toLowerCase();
  if (r === "all units") return "portfolio";
  if (r === "exterior") return "exterior";
  if (r.startsWith("bldg ") && r.includes("common")) return "common";
  if (/^\d{1,2}[a-c]$/i.test(room)) return "unit";
  return "other";
}

function statsForRoom(items, room) {
  const s = { total: 0, open: 0, in_progress: 0, complete: 0, deferred: 0, high_open: 0, urgent_open: 0, photo_gap: 0 };
  for (const it of items) {
    if (it.room !== room) continue;
    s.total++;
    s[it.status] = (s[it.status] || 0) + 1;
    if (it.status !== "complete" && it.priority === "high") s.high_open++;
    if (it.status !== "complete" && it.priority === "urgent") s.urgent_open++;
    const need = parseInt(it.min_photos || 0);
    const have = (it.photos || []).length;
    if (it.status !== "complete" && need > have) s.photo_gap += (need - have);
  }
  return s;
}

function Gauge({ pct, done, occupied, size = 44 }) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = occupied ? "#dc2626" : done ? "#10b981" : pct >= 66 ? "#3b82f6" : pct > 0 ? "#f59e0b" : "#cbd5e1";
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="unit-gauge">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize={size > 40 ? "10" : "8"} fontWeight="900" fill={color}>
        {occupied ? "🚫" : done ? "✓" : `${pct}%`}
      </text>
    </svg>
  );
}

function RoomTile({ label, sublabel, stats, onClick, testid, accent, occupied, occInfo }) {
  const pct = stats.total === 0 ? 0 : Math.round((stats.complete / stats.total) * 100);
  const done = stats.complete === stats.total && stats.total > 0;
  const fmt = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return d; }
  };
  const moveInDate = occInfo?.move_in_date
    || (occInfo?.next_visit?.kind === "move_in" ? occInfo.next_visit.date : "");
  const moveOutDate = occInfo?.move_out_date
    || (occInfo?.next_visit?.kind === "move_out" ? occInfo.next_visit.date : "");
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left p-3 bg-white border-2 ${occupied ? "border-red-600 bg-red-50" : accent || "border-[#0f172a]"} hover:shadow-[4px_4px_0px_#0f172a] transition-shadow relative min-h-[110px]`}>
      {occupied && (
        <div className="absolute top-1 right-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white" data-testid={`tile-occupied-${label}`}>
          🚫 Occupied
        </div>
      )}
      <div className="flex items-start gap-2">
        <Gauge pct={pct} done={done} occupied={occupied}/>
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-lg leading-tight uppercase tracking-tight truncate">{label}</div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mt-0.5">
            {stats.complete}/{stats.total} done
          </div>
          {moveInDate && (
            <div data-testid={`tile-move-in-${label}`}
              className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white">
              ➜ MOVE-IN {fmt(moveInDate)}
            </div>
          )}
          {moveOutDate && !moveInDate && (
            <div data-testid={`tile-move-out-${label}`}
              className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-600 text-white">
              ⬅ MOVE-OUT {fmt(moveOutDate)}
            </div>
          )}
          {stats.urgent_open > 0 && !occupied && (
            <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-pink-500 text-white animate-pulse">🚨 {stats.urgent_open} urgent</div>
          )}
          {stats.high_open > 0 && !occupied && (
            <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">🔥 {stats.high_open} priority</div>
          )}
          {stats.photo_gap > 0 && !occupied && (
            <div className="mt-1 inline-block text-[10px] font-black uppercase tracking-widest text-amber-700">📸 {stats.photo_gap} needed</div>
          )}
          {sublabel && <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest truncate mt-1">{sublabel}</div>}
        </div>
      </div>
    </button>
  );
}

export default function ProjectRadarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRoom, setActiveRoom] = useState(null);
  const [filters, setFilters] = useState({ status: "", category: "" });
  const [occupancy, setOccupancy] = useState({});
  const [camDeploy, setCamDeploy] = useState({ open: false, address: "", unit: "" });
  const [bulkMode, setBulkMode] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [allPhotos, setAllPhotos] = useState(null);
  const [photoFilter, setPhotoFilter] = useState({ by: "", room: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [codes, setCodes] = useState({ building_door_code: "", units: [] });
  const [codesEditor, setCodesEditor] = useState(null); // null | "building" | "all"
  const [codesDraft, setCodesDraft] = useState({ building_door_code: "", keys_location_note: "", laundry_room_key_code: "", unit_codes: {}, unit_key_codes: {}, unit_notes: {} });
  const [codesBusy, setCodesBusy] = useState(false);
  const [albumLinkEditor, setAlbumLinkEditor] = useState(false);
  const [albumLinkDraft, setAlbumLinkDraft] = useState("");
  const [albumTab, setAlbumTab] = useState("before");
  const [albumUploading, setAlbumUploading] = useState(false);
  const [compareOpen, setCompareOpen] = useState(null); // photo dict
  const [pairBusy, setPairBusy] = useState(false);
  const [compareMode, setCompareMode] = useState("split"); // split | slider
  const [sliderPct, setSliderPct] = useState(50);
  const [autoPairFor, setAutoPairFor] = useState(null); // { punchRoom, beforePhoto }
  const [assignPhoto, setAssignPhoto] = useState(null); // photo dict → shows punch-picker modal
  const [assignKeep, setAssignKeep] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignFilter, setAssignFilter] = useState("");
  const [showAIAssess, setShowAIAssess] = useState(false);
  const [showWalkMode, setShowWalkMode] = useState(false);
  const [showVoicePunch, setShowVoicePunch] = useState(false);
  const [tradeLinks, setTradeLinks] = useState([]);
  const [tradeLabel, setTradeLabel] = useState("");
  const [tradeChoice, setTradeChoice] = useState("cleaner");
  const [tradeUsePin, setTradeUsePin] = useState(false);
  const [tradeBusy, setTradeBusy] = useState(false);
  const [freshPin, setFreshPin] = useState(null); // { token, pin, url } — flashed once after mint

  const TRADES = [
    ["cleaner", "🧹 Cleaner"], ["plumber", "🔧 Plumber"],
    ["electrician", "⚡ Electrician"], ["painter", "🎨 Painter"],
    ["hvac", "❄️ HVAC"], ["handyman", "🔨 Handyman"],
    ["water_heater", "💧 Water Heater"], ["landscaper", "🌿 Landscaper"],
    ["roofer", "🏠 Roofer"], ["general_sub", "👷 Field Crew"],
  ];

  const loadTradeLinks = useCallback(async () => {
    if (!(user?.is_founder || user?.platform_role === "super_admin")) return;
    try {
      const r = await axios.get(`${BASE}/api/projects/${id}/tradesman-links`, auth());
      setTradeLinks(r.data.links || []);
    } catch { /* ignore — not fatal */ }
  }, [id, user]);
  useEffect(() => { loadTradeLinks(); }, [loadTradeLinks]);

  const mintTradeLink = async () => {
    setTradeBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/projects/${id}/tradesman-links`,
        { label: tradeLabel, trade: tradeChoice, use_pin: tradeUsePin }, auth());
      const url = `${window.location.origin}/tradesman/${r.data.token}`;
      if (r.data.pin_plaintext) {
        setFreshPin({ token: r.data.token, pin: r.data.pin_plaintext, url, label: r.data.label });
        toast.success(`Link + PIN created — text both to ${r.data.label}`);
      } else {
        toast.success("Link created — copy or text it now");
      }
      setTradeLabel("");
      setTradeUsePin(false);
      loadTradeLinks();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't create link"));
    } finally { setTradeBusy(false); }
  };

  const revokeTradeLink = async (tok) => {
    if (!window.confirm("Revoke this link? Whoever holds it will get a 'link not working' page.")) return;
    try {
      await axios.delete(`${BASE}/api/projects/${id}/tradesman-links/${tok}`, auth());
      toast.success("Link revoked");
      loadTradeLinks();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't revoke")); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pj, occ, ac] = await Promise.all([
        axios.get(`${BASE}/api/projects/${id}`, auth()),
        axios.get(`${BASE}/api/projects/${id}/occupancy`, auth()).catch(() => ({ data: { occupancy: {} }})),
        axios.get(`${BASE}/api/projects/${id}/access-codes`, auth()).catch(() => ({ data: { building_door_code: "", units: [] }})),
      ]);
      setProj(pj.data);
      setOccupancy(occ.data.occupancy || {});
      setCodes(ac.data || { building_door_code: "", units: [] });
      // Auto-open the flat "All Items" list when there are no unit-pattern rooms
      // (single-family / renovation projects like 6220 Carpenter). Multi-unit
      // buildings (Chew) still land on the tap-a-tile picker.
      const rms = pj.data?.rooms || [];
      const hasUnitRooms = rms.some(r => /^\d{1,2}[a-c]$/i.test(r));
      setActiveRoom(cur => cur ?? (!hasUnitRooms && rms.length > 0 ? "__all__" : null));
    } catch { toast.error("Couldn't load project"); nav("/admin/projects"); }
    finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const unitCodeFor = (unit) => {
    const row = (codes.units || []).find(x => x.unit === unit);
    return row?.unit_door_code || "";
  };
  const initialCodesRef = useRef({ building: "", keys_note: "", laundry: "", units: {}, keys: {}, notes: {} });
  const openCodesEditor = (mode) => {
    const codesMap = {}, keysMap = {}, notesMap = {};
    (codes.units || []).forEach(u => {
      if (!u.unit) return;
      codesMap[u.unit] = u.unit_door_code || "";
      keysMap[u.unit] = u.unit_key_code || "";
      notesMap[u.unit] = u.unit_note || "";
    });
    initialCodesRef.current = {
      building: codes.building_door_code || "",
      keys_note: codes.keys_location_note || "",
      laundry: codes.laundry_room_key_code || "",
      units: { ...codesMap }, keys: { ...keysMap }, notes: { ...notesMap },
    };
    setCodesDraft({
      building_door_code: codes.building_door_code || "",
      keys_location_note: codes.keys_location_note || "",
      laundry_room_key_code: codes.laundry_room_key_code || "",
      unit_codes: codesMap,
      unit_key_codes: keysMap,
      unit_notes: notesMap,
    });
    setCodesEditor(mode);
  };
  const saveCodes = async () => {
    setCodesBusy(true);
    try {
      const payload = {};
      const init = initialCodesRef.current;
      if (codesDraft.building_door_code !== init.building) payload.building_door_code = codesDraft.building_door_code;
      if (codesDraft.keys_location_note !== init.keys_note) payload.keys_location_note = codesDraft.keys_location_note;
      if (codesDraft.laundry_room_key_code !== init.laundry) payload.laundry_room_key_code = codesDraft.laundry_room_key_code;
      const diff = (draft, base) => {
        const out = {};
        Object.entries(draft || {}).forEach(([u, v]) => {
          if ((v || "") !== (base[u] || "")) out[u] = v || "";
        });
        return out;
      };
      const dc = diff(codesDraft.unit_codes, init.units);
      const dk = diff(codesDraft.unit_key_codes, init.keys);
      const dn = diff(codesDraft.unit_notes, init.notes);
      if (Object.keys(dc).length) payload.unit_codes = dc;
      if (Object.keys(dk).length) payload.unit_key_codes = dk;
      if (Object.keys(dn).length) payload.unit_notes = dn;
      if (!Object.keys(payload).length) {
        toast.info("Nothing changed");
        setCodesEditor(null);
        return;
      }
      await axios.patch(`${BASE}/api/projects/${id}/access-codes`, payload, auth());
      toast.success("Access codes saved");
      const { data } = await axios.get(`${BASE}/api/projects/${id}/access-codes`, auth());
      setCodes(data);
      setCodesEditor(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't save codes")); }
    finally { setCodesBusy(false); }
  };
  const saveSingleUnitCode = async (unit, code) => {
    try {
      await axios.patch(`${BASE}/api/projects/${id}/access-codes`,
        { unit_codes: { [unit]: code } }, auth());
      const { data } = await axios.get(`${BASE}/api/projects/${id}/access-codes`, auth());
      setCodes(data);
      toast.success(`Code saved for ${unit}`);
    } catch { toast.error("Couldn't save"); }
  };

  const shareProject = async () => {
    try {
      const { data } = await axios.post(`${BASE}/api/projects/${id}/share`, {}, auth());
      const url = `${window.location.origin}/project/${data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Read-only project link copied!", { duration: 4000, description: url });
    } catch { toast.error("Couldn't create share link"); }
  };
  const shareUnit = async (unit) => {
    try {
      let tok = proj.share_token;
      if (!tok) {
        const { data } = await axios.post(`${BASE}/api/projects/${id}/share`, {}, auth());
        tok = data.share_token;
        setProj({...proj, share_token: tok});
      }
      const url = `${window.location.origin}/project/${tok}/unit/${encodeURIComponent(unit)}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Read-only link for ${unit} copied!`, { duration: 4000, description: url });
    } catch { toast.error("Couldn't create share link"); }
  };
  const toggleOccupancy = async (unit, occupied) => {
    try {
      const { data: res } = await axios.post(`${BASE}/api/projects/${id}/units/${encodeURIComponent(unit)}/occupancy`,
        { occupied }, auth());
      setOccupancy(prev => ({...prev, [unit]: { occupied, occupied_at: occupied ? new Date().toISOString() : "" }}));
      toast.success(occupied ? `🚫 ${unit} marked occupied — staff will avoid` : `${unit} marked vacant`);
      // Refresh full occupancy map so any drift (e.g. vacancy_status vs occupied bool) resyncs
      try {
        const { data: fresh } = await axios.get(`${BASE}/api/projects/${id}/occupancy`, auth());
        if (fresh?.occupancy) setOccupancy(fresh.occupancy);
      } catch {}
      // Raza vacancy → prompt admin to deploy a ProCall spare camera
      if (!occupied && res?.raza_cam_suggestion) {
        setCamDeploy({
          open: true,
          address: res.address || proj?.property_address || "",
          unit,
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update occupancy";
      toast.error(msg);
    }
  };

  const toggleSelect = (itemId) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(itemId)) n.delete(itemId); else n.add(itemId);
      return n;
    });
  };
  const selectAllVisible = (visibleIds) => setSelected(new Set(visibleIds));
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (patch, force = false) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/projects/${id}/punch/bulk${force ? "?force=true" : ""}`,
        { item_ids: Array.from(selected), ...patch }, auth(),
      );
      if (data.skipped_count > 0 && patch.status === "complete" && !force) {
        const first = data.skipped.slice(0, 3).map(s => `• ${s.room} — ${s.reason}`).join("\n");
        const more = data.skipped.length > 3 ? `\n…and ${data.skipped.length - 3} more` : "";
        if (window.confirm(`${data.updated_count} marked complete. ${data.skipped_count} skipped (missing photos):\n\n${first}${more}\n\nOverride & force-complete the skipped ones anyway?`)) {
          setSelected(new Set(data.skipped.map(s => s.id)));
          return await runBulk(patch, true);
        }
        toast.success(`${data.updated_count} completed · ${data.skipped_count} left needing photos`);
      } else {
        toast.success(`${data.updated_count} item(s) updated`);
      }
      clearSelection();
      setBulkMode(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk update failed"));
    }
    finally { setBulkBusy(false); }
  };

  const allItems = proj?.punch_items || [];
  const items = allItems.filter(it => {
    if (activeRoom && activeRoom !== "__all__" && it.room !== activeRoom) return false;
    if (filters.status && it.status !== filters.status) return false;
    if (filters.category && it.category !== filters.category) return false;
    return true;
  });

  // Bucket rooms for the tap-a-tile picker
  const rooms = proj?.rooms || [];
  const unitRooms = rooms.filter(r => roomGroup(r) === "unit")
    .sort((a, b) => (parseInt(a) - parseInt(b)) || a.slice(-1).localeCompare(b.slice(-1)));
  const commonRooms = rooms.filter(r => roomGroup(r) === "common")
    .sort((a, b) => parseInt(a.match(/\d+/)?.[0] || 0) - parseInt(b.match(/\d+/)?.[0] || 0));

  const closeProject = async () => {
    if (!window.confirm("Close this project? You can still view it, but it will be marked complete.")) return;
    try {
      await axios.patch(`${BASE}/api/projects/${id}`, { status: "closed" }, auth());
      toast.success("Project closed");
      load();
    } catch { toast.error("Couldn't close"); }
  };
  const reopenProject = async () => {
    try {
      await axios.patch(`${BASE}/api/projects/${id}`, { status: "active" }, auth());
      toast.success("Project reopened");
      load();
    } catch { toast.error("Couldn't reopen"); }
  };

  const saveAlbumLink = async () => {
    try {
      await axios.patch(`${BASE}/api/projects/${id}`, { photos_url: albumLinkDraft.trim() }, auth());
      toast.success(albumLinkDraft ? "Album link saved" : "Album link cleared");
      setAlbumLinkEditor(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't save")); }
  };
  // Load all-photos when the gallery opens (memoized per project id)
  useEffect(() => {
    if (!showAllPhotos) return;
    let cancelled = false;
    (async () => {
      try {
        setAllPhotos(null);
        const { data } = await axios.get(`${BASE}/api/projects/${id}/all-photos`, auth());
        if (!cancelled) setAllPhotos(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't load photos"));
      }
    })();
    return () => { cancelled = true; };
  }, [showAllPhotos, id]);
  const uploadProjectPhoto = async (files, kind, roomHint = "") => {
    if (!files || files.length === 0) return null;
    setAlbumUploading(true);
    let ok = 0, fail = 0, lastEntry = null;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({ kind });
      if (roomHint) qs.set("room", roomHint);
      try {
        const { data } = await axios.post(`${BASE}/api/projects/${id}/photos?${qs}`, fd, {
          ...auth(), headers: { ...(auth().headers || {}), "Content-Type": "multipart/form-data" },
        });
        ok += 1; lastEntry = data;
      } catch { fail += 1; }
    }
    setAlbumUploading(false);
    if (ok) toast.success(`Uploaded ${ok} · ${kind}`);
    if (fail) toast.error(`${fail} failed`);
    await load();
    return lastEntry;
  };
  const deleteProjectPhoto = async (fileId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${BASE}/api/projects/${id}/photos/${fileId}`, auth());
      toast.success("Deleted");
      load();
    } catch { toast.error("Couldn't delete"); }
  };
  const pairPhotos = async (beforeId, afterId) => {
    setPairBusy(true);
    try {
      await axios.post(`${BASE}/api/projects/${id}/photos/pair`,
        { before_id: beforeId, after_id: afterId }, auth());
      toast.success("Paired · Before + After linked");
      const { data } = await axios.get(`${BASE}/api/projects/${id}`, auth());
      setProj(data);
      // Re-open compare on the current photo (its pair_id will now be set)
      const refreshed = (data.project_photos || []).find(p => p.id === compareOpen?.id);
      if (refreshed) setCompareOpen(refreshed);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't pair")); }
    finally { setPairBusy(false); }
  };
  const assignAlbumPhotoToPunch = async (fileId, punchItemId) => {
    setAssignBusy(true);
    try {
      await axios.post(`${BASE}/api/projects/${id}/photos/${fileId}/assign-to-punch`,
        { punch_item_id: punchItemId, keep_in_album: assignKeep }, auth());
      toast.success(assignKeep ? "Copied onto punch item" : "Moved onto punch item");
      setAssignPhoto(null);
      setAssignKeep(false);
      setAssignFilter("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't assign photo"));
    } finally { setAssignBusy(false); }
  };
  const unpairPhoto = async (fileId) => {
    setPairBusy(true);
    try {
      await axios.delete(`${BASE}/api/projects/${id}/photos/${fileId}/pair`, auth());
      toast.success("Unpaired");
      const { data } = await axios.get(`${BASE}/api/projects/${id}`, auth());
      setProj(data);
      const refreshed = (data.project_photos || []).find(p => p.id === compareOpen?.id);
      if (refreshed) setCompareOpen(refreshed);
    } catch { toast.error("Couldn't unpair"); }
    finally { setPairBusy(false); }
  };

  if (loading) return <AdminShell><div className="p-6 text-center font-black uppercase text-slate-400 text-xs">Loading…</div></AdminShell>;
  if (!proj) return null;

  const k = proj.kpis || {};

  return (
    <AdminShell>
    <div className="p-3 sm:p-6" data-testid="project-detail-page">
      <div className="mb-3">
        <button onClick={() => nav("/admin/projects")} data-testid="project-back"
          className="text-xs font-black uppercase tracking-widest text-slate-600 hover:text-black inline-flex items-center gap-1">
          <ArrowLeft weight="bold" size={12}/> All Projects
        </button>
      </div>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight" data-testid="project-title">{proj.name}</h1>
          <div className="text-sm text-slate-600 mt-0.5">
            {proj.property_address}{proj.unit ? ` · Unit ${proj.unit}` : ""}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-fuchsia-600 text-white">{proj.kind?.replace(/_/g," ")}</span>
            <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${proj.status === "active" ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"}`}>{proj.status}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => openCodesEditor("all")} data-testid="access-codes-btn"
            className={`h-10 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1 ${
              codes.building_door_code ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-white hover:bg-slate-50 text-slate-700 border-dashed"
            }`}
            title={codes.building_door_code ? `Building door · ${codes.building_door_code}` : "No codes yet — click to add"}>
            🔑 {codes.building_door_code ? `Bldg ${codes.building_door_code}` : "Add Access Codes"}
          </button>
          {proj.photos_url ? (
            <a href={proj.photos_url} target="_blank" rel="noreferrer"
              data-testid="album-chip"
              title={proj.photos_url}
              className="h-10 px-3 bg-pink-500 hover:bg-pink-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              📸 Photo Album
            </a>
          ) : (
            <button onClick={() => setAlbumLinkEditor(true)} data-testid="album-add-link-btn"
              className="h-10 px-3 bg-white hover:bg-pink-50 text-pink-700 font-black uppercase text-[10px] tracking-widest border-2 border-pink-500 border-dashed inline-flex items-center gap-1">
              📸 + Album Link
            </button>
          )}
          {proj.photos_url && (
            <button onClick={() => setAlbumLinkEditor(true)} data-testid="album-edit-link-btn"
              title="Edit album URL"
              className="h-10 px-2 bg-white hover:bg-slate-50 text-slate-600 border-2 border-slate-300 font-black">✎</button>
          )}
          <button onClick={shareProject} data-testid="project-share-btn"
            className="h-10 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
            🔗 Share Read-Only Link
          </button>
          {(proj.punch_items?.length || 0) > 0 && (
            <button onClick={() => setShowWalkMode(true)} data-testid="project-walk-btn"
              className="h-10 px-3 bg-[#00C4CC] hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              🚶 Start Walk
            </button>
          )}
          <button onClick={() => setShowAllPhotos(true)} data-testid="project-all-photos-btn"
            className="h-10 px-3 bg-purple-700 hover:bg-purple-800 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
            🖼 All Photos
          </button>
          <a href={`${BASE}/api/projects/${id}/handoff.pdf?auth=${encodeURIComponent(jwt())}`}
            target="_blank" rel="noreferrer" data-testid="project-handoff-pdf-btn"
            className="h-10 px-3 bg-emerald-700 hover:bg-emerald-800 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
            📄 Handoff PDF
          </a>
          <button onClick={() => setShowVoicePunch(true)} data-testid="project-voice-punch-btn"
            className="h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
            <Microphone weight="fill" size={14}/> Voice Add Items
          </button>
          {(proj.punch_items?.length || 0) > 0 && (
            <button onClick={() => setShowAIAssess(true)} data-testid="project-ai-btn"
              className="h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              ✨ AI Estimate
            </button>
          )}
          <button onClick={async () => {
              if (!window.confirm("Scan every un-scanned photo on this project for appliance nameplates and add matches to Assets?\n\nThis uses Gemini 3 Flash vision (~$0.0002/photo).")) return;
              const t = toast.loading("Scanning photos for nameplates…", { duration: 180000 });
              try {
                const r = await axios.post(
                  `${BASE}/api/admin/rescan-punch-photos-for-appliances`,
                  {}, { ...auth(), params: { address: proj.property_address, limit: 500 }, timeout: 300000 });
                const d = r.data || {};
                toast.dismiss(t);
                toast.success(
                  `Scanned ${d.photos_scanned} · ${d.nameplates_found} nameplates found · ${d.assets_inserted} new appliances added`,
                  { duration: 8000 });
              } catch (e) {
                toast.dismiss(t);
                toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed"));
              }
            }} data-testid="project-scan-nameplates-btn"
            className="h-10 px-3 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
            📸→📦 Scan Nameplates
          </button>
          {proj.private && (
            <span className="h-10 px-3 flex items-center bg-violet-100 text-violet-800 font-black uppercase text-[10px] tracking-widest border-2 border-violet-600" data-testid="private-badge">
              🔒 Private · Only You
            </span>
          )}
          {proj.status === "active" ? (
            <button onClick={closeProject} data-testid="project-close-btn"
              className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
              Close Project
            </button>
          ) : (
            <button onClick={reopenProject} data-testid="project-reopen-btn"
              className="h-10 px-3 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
              Reopen
            </button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="project-kpis">
        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{t("pr.kpi.total")}</div>
          <div className="font-display font-black text-2xl">{k.total || 0}</div>
        </div>
        <div className="p-3 bg-amber-50 border-2 border-amber-500">
          <div className="text-[9px] font-black uppercase tracking-widest text-amber-700">{t("pr.kpi.open")}</div>
          <div className="font-display font-black text-2xl text-amber-700">{k.open || 0}</div>
        </div>
        <div className="p-3 bg-blue-50 border-2 border-blue-500">
          <div className="text-[9px] font-black uppercase tracking-widest text-blue-700">In Progress</div>
          <div className="font-display font-black text-2xl text-blue-700">{k.in_progress || 0}</div>
        </div>
        <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
          <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Complete</div>
          <div className="font-display font-black text-2xl text-emerald-700">{k.complete || 0}</div>
        </div>
        {k.cost_cents !== undefined && (
          <div className="p-3 bg-white border-2 border-[#0f172a]" data-testid="kpi-est-cost">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Est Cost</div>
            <div className="font-display font-black text-2xl">${(k.cost_cents/100).toFixed(0)}</div>
          </div>
        )}
      </div>

      <ScheduledVisitsPanel projectId={proj.id} visits={proj.scheduled_visits || []}
        address={proj.property_address}
        onChange={(next) => setProj({ ...proj, scheduled_visits: next })}/>
      <LeftBehindRoundUp address={proj.property_address}/>

      {activeRoom ? (
        <>
          {/* Room drilled-in view: back button + status/category filters + item list */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button onClick={() => setActiveRoom(null)} data-testid="room-back-btn"
              className="h-9 px-3 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <ArrowLeft weight="bold" size={12}/> {unitRooms.length > 0 ? "Back to Units" : "Room View"}
            </button>
            <div className="font-display font-black text-xl uppercase tracking-tight" data-testid="active-room-label">
              {activeRoom === "__all__" ? `📋 All Items (${items.length})` : `📍 ${activeRoom}`}
            </div>
            {/^\d{1,2}[a-c]$/i.test(activeRoom || "") && (
              <>
                <button onClick={() => toggleOccupancy(activeRoom, !occupancy[activeRoom]?.occupied)} data-testid="unit-occupancy-toggle"
                  className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] ${
                    occupancy[activeRoom]?.occupied ? "bg-red-600 hover:bg-red-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"
                  }`}>
                  {occupancy[activeRoom]?.occupied ? "🚫 Occupied — Do Not Enter" : "🏠 Mark as Occupied"}
                </button>
                <button onClick={() => shareUnit(activeRoom)} data-testid="unit-share-btn"
                  className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
                  🔗 Share Unit
                </button>
                <UnitCodePill unit={activeRoom}
                  buildingCode={codes.building_door_code}
                  unitCode={unitCodeFor(activeRoom)}
                  unitKeyCode={(codes.units || []).find(x => x.unit === activeRoom)?.unit_key_code || ""}
                  unitNote={(codes.units || []).find(x => x.unit === activeRoom)?.unit_note || ""}
                  onSave={(code) => saveSingleUnitCode(activeRoom, code)}/>
              </>
            )}
            <div className="flex-1"/>
            <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} data-testid="filter-status" className="h-9 px-2 border-2 border-slate-300 text-xs bg-white">
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} data-testid="filter-category" className="h-9 px-2 border-2 border-slate-300 text-xs bg-white">
              <option value="">All Categories</option>
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={() => { setBulkMode(!bulkMode); clearSelection(); }} data-testid="bulk-mode-toggle"
              className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] ${bulkMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white hover:bg-slate-50"}`}>
              {bulkMode ? "✕ Exit Bulk" : "☑ Bulk Select"}
            </button>
            {bulkMode && (
              <button onClick={() => selectAllVisible(items.map(x => x.id))} data-testid="bulk-select-all"
                className="h-9 px-3 bg-white hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest border-2 border-slate-300">
                Select All ({items.length})
              </button>
            )}
          </div>
          <div className="space-y-2" data-testid="punch-list">
            <AddItemInline projectId={id} rooms={rooms} defaultRoom={activeRoom} onAdded={(item) => {
              const next = [...allItems, item];
              setProj({...proj, punch_items: next, kpis: computeKpis(next)});
            }}/>
            {items.length === 0 && (
              <div className="p-6 bg-white border-2 border-dashed border-slate-300 text-center text-xs text-slate-500" data-testid="punch-empty">
                No items for <b>{activeRoom}</b> match the current filters.
              </div>
            )}
            {(() => {
              const walkthroughItems = items.filter(x => x.discovery !== "post");
              const postItems = items.filter(x => x.discovery === "post");
              const renderRow = (it) => (
                <PunchRow key={it.id} projectId={id} item={it}
                  bulkMode={bulkMode} selected={selected.has(it.id)} onToggleSelect={toggleSelect}
                  onChanged={(upd) => {
                    const prev = allItems.find(x => x.id === upd.id);
                    const next = allItems.map(x => x.id === upd.id ? upd : x);
                    setProj({...proj, punch_items: next, kpis: computeKpis(next)});
                    // Auto-Pair by Room: when a punch flips to complete, check for an
                    // unpaired Before in the same room and prompt for an After upload.
                    if (prev && prev.status !== "complete" && upd.status === "complete" && upd.room) {
                      const orphanBefore = (proj.project_photos || []).find(
                        p => p.kind === "before" && !p.pair_id && (p.room || "") === upd.room
                      );
                      if (orphanBefore) setAutoPairFor({ punchRoom: upd.room, beforePhoto: orphanBefore, punchId: upd.id });
                    }
                  }}
                  onRemoved={(rid) => {
                    const next = allItems.filter(x => x.id !== rid);
                    setProj({...proj, punch_items: next, kpis: computeKpis(next)});
                  }}
                />
              );
              return (
                <>
                  {walkthroughItems.length > 0 && postItems.length > 0 && (
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-2">📋 Walkthrough items ({walkthroughItems.length})</div>
                  )}
                  {walkthroughItems.map(renderRow)}
                  {postItems.length > 0 && (
                    <div className="mt-4 p-2 bg-pink-500 text-white font-black uppercase text-[10px] tracking-widest" data-testid="new-finds-header">
                      🆕 New Issues Found ({postItems.length}) — post-walkthrough discoveries
                    </div>
                  )}
                  {postItems.map(renderRow)}
                </>
              );
            })()}
          </div>
        </>
      ) : (
        <>
          {/* Room-picker (tap-a-tile) view */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Tap a tile below to work that unit or area's checklist
            </div>
            <ImportTurnKitButton projectId={id} defaultRoom="" onImported={load}/>
          </div>

          {/* Portfolio-wide + Exterior tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="picker-portfolio">
            {rooms.includes("All Units") && (
              <RoomTile label="🏢 All Units" sublabel="Portfolio-wide admin (project-only)"
                stats={statsForRoom(allItems, "All Units")}
                onClick={() => setActiveRoom("All Units")} testid="room-tile-all-units"
                accent="border-violet-600"/>
            )}
            {rooms.includes("Exterior") && (
              <RoomTile label="🌳 Exterior" sublabel="Gates · lighting · dumpster · doors"
                stats={statsForRoom(allItems, "Exterior")}
                onClick={() => setActiveRoom("Exterior")} testid="room-tile-exterior"
                accent="border-emerald-600"/>
            )}
          </div>

          {/* Units */}
          {unitRooms.length > 0 && (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 mb-2">🏠 Units ({unitRooms.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4" data-testid="picker-units">
                {unitRooms.map(r => (
                  <RoomTile key={r} label={r} sublabel=""
                    stats={statsForRoom(allItems, r)}
                    occupied={!!occupancy[r]?.occupied}
                    occInfo={occupancy[r]}
                    onClick={() => setActiveRoom(r)} testid={`room-tile-${r}`}
                    accent="border-fuchsia-300"/>
                ))}
              </div>
            </>
          )}

          {/* Common areas */}
          {commonRooms.length > 0 && (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 mb-2">🚪 Building Common Areas ({commonRooms.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4" data-testid="picker-common">
                {commonRooms.map(r => (
                  <RoomTile key={r} label={r.replace(" Common", "")}
                    sublabel="hallway · laundry · water tank"
                    stats={statsForRoom(allItems, r)}
                    onClick={() => setActiveRoom(r)} testid={`room-tile-${r}`}
                    accent="border-cyan-300"/>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {/* Access Codes Editor Modal */}
      {codesEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-10 p-4 overflow-y-auto"
          data-testid="access-codes-modal" onClick={() => !codesBusy && setCodesEditor(null)}>
          <div className="w-full max-w-3xl bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#f59e0b]" onClick={e => e.stopPropagation()}>
            <div className="p-3 bg-amber-500 text-white flex items-center justify-between">
              <div className="font-display font-black text-lg uppercase tracking-tight">🔑 Access Codes</div>
              <button onClick={() => !codesBusy && setCodesEditor(null)} className="text-white/80 hover:text-white font-black">✕</button>
            </div>
            <div className="p-3 space-y-3">
              <div className="p-2 bg-amber-50 border-2 border-amber-500 text-[10px] font-black uppercase tracking-widest text-amber-800">
                🔒 Admin + Landlord Portal only · Never shown in public share links
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Building door code</label>
                  <input value={codesDraft.building_door_code}
                    onChange={e => setCodesDraft({...codesDraft, building_door_code: e.target.value})}
                    placeholder="e.g. 4231#"
                    data-testid="codes-building-input"
                    className="w-full h-11 px-3 border-2 border-slate-300 text-sm mt-1 font-mono"/>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Laundry-room key ID</label>
                  <input value={codesDraft.laundry_room_key_code}
                    onChange={e => setCodesDraft({...codesDraft, laundry_room_key_code: e.target.value})}
                    placeholder="e.g. 3858926 (A Keyway)"
                    data-testid="codes-laundry-input"
                    className="w-full h-11 px-3 border-2 border-slate-300 text-sm mt-1 font-mono"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Keys-location note (property-wide)</label>
                <input value={codesDraft.keys_location_note}
                  onChange={e => setCodesDraft({...codesDraft, keys_location_note: e.target.value})}
                  placeholder="e.g. Physical keys for letter doors stored in LB in laundry closet"
                  data-testid="codes-keys-note-input"
                  className="w-full h-11 px-3 border-2 border-slate-300 text-sm mt-1"/>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
                  {t("pr.codes.per_unit")}
                </div>
                <div className="max-h-[45vh] overflow-y-auto border border-slate-200 divide-y divide-slate-100">
                  {(codes.units || [])
                    .filter(u => /^\d{1,2}[a-c]$/i.test(u.unit))
                    .sort((a, b) => (parseInt(a.unit) - parseInt(b.unit)) || a.unit.slice(-1).localeCompare(b.unit.slice(-1)))
                    .map(u => (
                    <div key={u.unit} className="p-1.5 grid grid-cols-12 gap-1.5 items-center hover:bg-slate-50">
                      <span className="col-span-1 text-[10px] font-black uppercase tracking-widest text-slate-500">{u.unit}</span>
                      <input value={codesDraft.unit_codes[u.unit] || ""}
                        onChange={e => setCodesDraft({...codesDraft, unit_codes: {...codesDraft.unit_codes, [u.unit]: e.target.value}})}
                        placeholder={t("pr.codes.pb_placeholder")}
                        data-testid={`codes-unit-${u.unit}`}
                        className="col-span-3 h-9 px-2 border border-slate-300 text-xs font-mono"/>
                      <input value={codesDraft.unit_key_codes[u.unit] || ""}
                        onChange={e => setCodesDraft({...codesDraft, unit_key_codes: {...codesDraft.unit_key_codes, [u.unit]: e.target.value}})}
                        placeholder={t("pr.codes.key_placeholder")}
                        data-testid={`codes-key-${u.unit}`}
                        className="col-span-3 h-9 px-2 border border-slate-300 text-xs font-mono"/>
                      <input value={codesDraft.unit_notes[u.unit] || ""}
                        onChange={e => setCodesDraft({...codesDraft, unit_notes: {...codesDraft.unit_notes, [u.unit]: e.target.value}})}
                        placeholder={t("pr.codes.note_placeholder")}
                        data-testid={`codes-note-${u.unit}`}
                        className="col-span-5 h-9 px-2 border border-slate-300 text-xs"/>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-3 bg-slate-100 border-t-2 border-[#0f172a] flex gap-2">
              <button onClick={() => setCodesEditor(null)} disabled={codesBusy}
                className="flex-1 h-11 border-2 border-slate-300 font-black uppercase text-xs tracking-widest bg-white hover:bg-slate-50">
                {t("pr.btn.cancel")}
              </button>
              <button onClick={saveCodes} disabled={codesBusy} data-testid="codes-save-btn"
                className="flex-1 h-11 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
                {codesBusy ? t("pr.btn.saving") : t("pr.codes.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Album link editor modal */}
      {albumLinkEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-10 p-4"
          data-testid="album-link-modal" onClick={() => setAlbumLinkEditor(false)}>
          <div className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#ec4899]" onClick={e => e.stopPropagation()}>
            <div className="p-3 bg-pink-500 text-white flex items-center justify-between">
              <div className="font-display font-black text-lg uppercase tracking-tight">📸 Photo Album Link</div>
              <button onClick={() => setAlbumLinkEditor(false)} className="text-white/80 hover:text-white font-black">✕</button>
            </div>
            <div className="p-3 space-y-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">External album URL (Google Photos, Dropbox, etc.)</div>
              <input value={albumLinkDraft || proj.photos_url || ""}
                onChange={e => setAlbumLinkDraft(e.target.value)}
                placeholder="https://photos.app.goo.gl/…"
                autoFocus data-testid="album-link-input"
                className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
              <div className="text-[10px] text-slate-500">Field workers get a one-tap 📸 button on the header. Not shown on public share links.</div>
            </div>
            <div className="p-3 bg-slate-100 border-t-2 border-[#0f172a] flex gap-2">
              <button onClick={() => setAlbumLinkEditor(false)}
                className="flex-1 h-11 border-2 border-slate-300 font-black uppercase text-xs tracking-widest bg-white hover:bg-slate-50">Cancel</button>
              {proj.photos_url && (
                <button onClick={() => { setAlbumLinkDraft(""); saveAlbumLink(); }}
                  className="h-11 px-3 border-2 border-red-300 text-red-700 hover:bg-red-50 font-black uppercase text-xs tracking-widest">
                  Clear
                </button>
              )}
              <button onClick={saveAlbumLink} data-testid="album-link-save"
                className="flex-1 h-11 bg-pink-500 hover:bg-pink-600 text-white font-black uppercase text-xs tracking-widest">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project album (in-app Before / After / Other) */}
      <section data-testid="project-album-section" className="bg-white border-2 border-[#0f172a] mb-3">
        <div className="p-2 flex items-center gap-2 flex-wrap border-b-2 border-slate-100">
          <div className="font-display font-black text-sm uppercase tracking-tight">📸 Project Album</div>
          {["before", "after", "other"].map(t => {
            const count = (proj.project_photos || []).filter(p => p.kind === t).length;
            const accent = t === "before" ? "orange" : t === "after" ? "emerald" : "slate";
            return (
              <button key={t} onClick={() => setAlbumTab(t)} data-testid={`album-tab-${t}`}
                className={`h-8 px-3 font-black uppercase text-[10px] tracking-widest border-2 ${
                  albumTab === t ? `bg-${accent}-500 text-white border-${accent}-600` : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}>
                {t}{count > 0 && <span className="ml-1 opacity-80">({count})</span>}
              </button>
            );
          })}
          <label className="ml-auto h-8 px-3 bg-[#00C4CC] hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] cursor-pointer inline-flex items-center gap-1" data-testid={`album-upload-${albumTab}`}>
            {albumUploading ? "Uploading…" : `+ Upload ${albumTab}`}
            <input type="file" multiple accept="image/*" className="hidden"
              onChange={e => { uploadProjectPhoto(e.target.files, albumTab, activeRoom && activeRoom !== "__all__" ? activeRoom : ""); e.target.value = ""; }} disabled={albumUploading}/>
          </label>
        </div>
        <div className="p-2">
          {(proj.project_photos || []).filter(p => p.kind === albumTab).length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400 font-black uppercase tracking-widest border border-dashed border-slate-200">
              No {albumTab} photos yet · tap upload above
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
              {(proj.project_photos || []).filter(p => p.kind === albumTab).map(p => {
                const src = `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(p.path)}&auth=${encodeURIComponent(jwt())}`;
                return (
                <div key={p.id} className="relative group aspect-square bg-slate-100 border border-slate-200" data-testid={`album-photo-${p.id}`}>
                  <button onClick={() => setCompareOpen(p)} className="block w-full h-full" title="Tap to compare">
                    <img src={src} alt={p.caption || albumTab} className="w-full h-full object-cover"/>
                  </button>
                  {p.pair_id && (
                    <span className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-fuchsia-600 text-white text-[9px] font-black uppercase tracking-widest" title="Has a matching pair">🔗 Paired</span>
                  )}
                  <button onClick={() => { setAssignPhoto(p); setAssignKeep(false); setAssignFilter(""); }} data-testid={`album-photo-assign-${p.id}`}
                    title="Assign to punch item"
                    className="absolute top-0.5 right-8 h-6 px-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[9px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100">→ Item</button>
                  <button onClick={() => deleteProjectPhoto(p.id)} data-testid={`album-photo-del-${p.id}`}
                    className="absolute top-0.5 right-0.5 h-6 w-6 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black opacity-0 group-hover:opacity-100">✕</button>
                  {p.uploaded_by && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] font-black uppercase tracking-widest px-1 py-0.5 truncate">{p.uploaded_by}</div>}
                </div>
              );})}
            </div>
          )}
        </div>
      </section>

      {/* Auto-Pair prompt — fires when a punch flips to complete and a Before
          photo tagged with the same room is still unpaired. */}
      {autoPairFor && (() => {
        const token = jwt();
        const beforeSrc = `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(autoPairFor.beforePhoto.path)}&auth=${encodeURIComponent(token)}`;
        return (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
            data-testid="auto-pair-modal" onClick={() => setAutoPairFor(null)}>
            <div className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#10b981]" onClick={e => e.stopPropagation()}>
              <div className="p-3 bg-emerald-500 text-white flex items-center justify-between">
                <div className="font-display font-black uppercase text-lg tracking-tight">✓ Punch Complete — Add After?</div>
                <button onClick={() => setAutoPairFor(null)} className="text-white/80 hover:text-white font-black">✕</button>
              </div>
              <div className="p-3 space-y-3">
                <div className="text-xs text-slate-600">
                  There's an unpaired <b className="text-orange-600">Before</b> photo tagged <b>{autoPairFor.punchRoom}</b>.
                  Upload the matching After and I'll pair them automatically.
                </div>
                <div className="grid grid-cols-2 gap-2 items-center">
                  <figure className="bg-black">
                    <div className="p-1 bg-orange-500 text-white font-black uppercase text-[9px] tracking-widest text-center">Before</div>
                    <img src={beforeSrc} alt="Before" className="w-full h-32 object-cover bg-black"/>
                  </figure>
                  <label className="h-32 border-2 border-dashed border-emerald-500 flex flex-col items-center justify-center gap-1 cursor-pointer bg-emerald-50 hover:bg-emerald-100"
                    data-testid="auto-pair-upload">
                    <span className="text-3xl">📸</span>
                    <span className="font-black uppercase text-[10px] tracking-widest text-emerald-700 text-center px-2">Choose After Photo</span>
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async e => {
                        const files = e.target.files;
                        e.target.value = "";
                        if (!files || files.length === 0) return;
                        const after = await uploadProjectPhoto(files, "after", autoPairFor.punchRoom);
                        if (after?.id) {
                          try {
                            await axios.post(`${BASE}/api/projects/${id}/photos/pair`,
                              { before_id: autoPairFor.beforePhoto.id, after_id: after.id }, auth());
                            toast.success("Paired · Before + After linked");
                            load();
                          } catch { toast.error("Uploaded but couldn't pair"); }
                        }
                        setAutoPairFor(null);
                      }}/>
                  </label>
                </div>
                <button onClick={() => setAutoPairFor(null)} data-testid="auto-pair-skip"
                  className="w-full h-9 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest bg-white hover:bg-slate-50">
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Assign album photo → Punch Item picker */}
      {assignPhoto && (() => {
        const token = jwt();
        const src = `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(assignPhoto.path)}&auth=${encodeURIComponent(token)}`;
        const q = assignFilter.trim().toLowerCase();
        const allPunch = proj.punch_items || [];
        const filtered = q ? allPunch.filter(it =>
          (it.title || "").toLowerCase().includes(q) ||
          (it.room || "").toLowerCase().includes(q) ||
          (it.unit || "").toLowerCase().includes(q) ||
          (it.category || "").toLowerCase().includes(q)
        ) : allPunch;
        // Group by room / unit for scan-ability
        const groups = {};
        filtered.forEach(it => {
          const key = it.unit || it.room || "— No room —";
          (groups[key] = groups[key] || []).push(it);
        });
        const groupKeys = Object.keys(groups).sort((a, b) => (a.length - b.length) || a.localeCompare(b));
        return (
          <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
            data-testid="assign-photo-modal" onClick={() => !assignBusy && setAssignPhoto(null)}>
            <div className="w-full max-w-2xl max-h-[90vh] bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#c026d3] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-3 bg-fuchsia-600 text-white flex items-center justify-between">
                <div className="font-display font-black uppercase text-lg tracking-tight">→ Assign Photo to Punch Item</div>
                <button onClick={() => !assignBusy && setAssignPhoto(null)} className="text-white/80 hover:text-white font-black" data-testid="assign-photo-close">✕</button>
              </div>
              <div className="p-3 border-b-2 border-slate-100 flex items-center gap-3">
                <img src={src} alt="Album" className="h-16 w-16 object-cover border-2 border-slate-300 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-black uppercase text-[10px] tracking-widest text-slate-500">Moving from album</div>
                  <div className="text-xs text-slate-600 truncate">{assignPhoto.kind} · {assignPhoto.room || "no room"} · by {assignPhoto.uploaded_by || "—"}</div>
                  <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                    <input type="checkbox" checked={assignKeep} onChange={e => setAssignKeep(e.target.checked)}
                      data-testid="assign-photo-keep" className="h-4 w-4 accent-fuchsia-600"/>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Keep copy in album too</span>
                  </label>
                </div>
              </div>
              <div className="p-3 border-b-2 border-slate-100">
                <input type="text" value={assignFilter} onChange={e => setAssignFilter(e.target.value)}
                  placeholder="Filter by title, room, unit, category…"
                  data-testid="assign-photo-filter"
                  className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {groupKeys.length === 0 && (
                  <div className="p-4 text-center text-xs text-slate-400 font-black uppercase tracking-widest border border-dashed border-slate-200">
                    No punch items match — clear the filter or add one to the project first.
                  </div>
                )}
                {groupKeys.map(gk => (
                  <div key={gk} data-testid={`assign-group-${gk}`}>
                    <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 mb-1">{gk}</div>
                    <div className="space-y-1">
                      {groups[gk].map(it => {
                        const catColor = CAT_COLOR[it.category] || "#475569";
                        const photoCount = (it.photos || []).length;
                        return (
                          <button key={it.id} onClick={() => assignAlbumPhotoToPunch(assignPhoto.id, it.id)}
                            disabled={assignBusy} data-testid={`assign-punch-${it.id}`}
                            className="w-full text-left p-2 bg-white border-2 border-slate-200 hover:border-fuchsia-500 hover:bg-fuchsia-50 disabled:opacity-40"
                            style={{ borderLeftWidth: "6px", borderLeftColor: catColor }}>
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_BG[it.status] || STATUS_BG.open}`}>{STATUS_LABEL[it.status] || it.status}</span>
                              <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: catColor }}>{it.category}</span>
                              {photoCount > 0 && <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">📸 {photoCount}</span>}
                            </div>
                            <div className="text-sm font-black">{it.title}</div>
                            {it.description && <div className="text-[11px] text-slate-500 italic truncate">{it.description}</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t-2 border-slate-100 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-500">
                <span>{filtered.length} of {allPunch.length} items</span>
                {assignBusy && <span className="text-fuchsia-600 animate-pulse">Assigning…</span>}
              </div>
            </div>
          </div>
        );
      })()}



      {/* All Photos gallery — every photo across every punch item + project
          album for this project, browsable in a single modal. */}
      {showAllPhotos && (() => {
        const token = jwt();
        // Route by source: walk photos live under /api/walk-reports/photo/{path},
        // punch + album photos live under the project's photo proxy.
        const src = (path, source) => source === "walk"
          ? `${BASE}/api/walk-reports/photo/${encodeURI(path)}?auth=${encodeURIComponent(token)}`
          : `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(token)}`;
        const filtered = ((allPhotos && allPhotos.photos) || []).filter(p => {
          if (photoFilter.by && p.by !== photoFilter.by) return false;
          if (photoFilter.room && p.room !== photoFilter.room) return false;
          return true;
        });
        return (
        <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" data-testid="project-all-photos-modal"
          onClick={() => setShowAllPhotos(false)}>
          <div className="bg-white max-w-6xl w-full mx-auto max-h-[95vh] my-4 flex flex-col"
            onClick={e => e.stopPropagation()}>
            <div className="p-3 sm:p-4 border-b-2 border-[#0f172a] flex items-center justify-between gap-3 flex-wrap sticky top-0 bg-white">
              <div className="min-w-0">
                <div className="font-display font-black text-xl sm:text-2xl uppercase tracking-tight">
                  🖼 All Photos {allPhotos ? `· ${filtered.length}/${allPhotos.total}` : ""}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {proj.property_address}{proj.unit ? ` · Unit ${proj.unit}` : ""} — {proj.name}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {allPhotos && (
                  <>
                    <select value={photoFilter.by} onChange={e => setPhotoFilter(f => ({...f, by: e.target.value}))}
                      data-testid="all-photos-filter-by"
                      className="h-9 px-2 text-xs border-2 border-slate-300 font-semibold">
                      <option value="">All uploaders</option>
                      {(allPhotos.uploaders || []).map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <select value={photoFilter.room} onChange={e => setPhotoFilter(f => ({...f, room: e.target.value}))}
                      data-testid="all-photos-filter-room"
                      className="h-9 px-2 text-xs border-2 border-slate-300 font-semibold">
                      <option value="">All rooms</option>
                      {(allPhotos.rooms || []).map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </>
                )}
                <button onClick={() => setShowAllPhotos(false)} data-testid="all-photos-close"
                  className="h-9 w-9 bg-red-600 hover:bg-red-700 text-white border-2 border-[#0f172a] flex items-center justify-center font-black">✕</button>
              </div>
            </div>
            <div className="p-3 sm:p-4 overflow-y-auto flex-1">
              {!allPhotos && <div className="text-center text-slate-400 py-10 font-black uppercase text-xs">Loading photos…</div>}
              {allPhotos && filtered.length === 0 && (
                <div className="text-center text-slate-400 py-10 font-black uppercase text-xs">No photos match this filter</div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {filtered.map((p, i) => {
                  const stamp = p.at ? new Date(p.at).toLocaleDateString(undefined, {month:"short", day:"numeric", year:"numeric"}) : "";
                  const url = src(p.path, p.source);
                  return (
                    <div key={i} className="bg-slate-50 border-2 border-slate-300 relative"
                      data-testid={`all-photos-tile-${i}`}>
                      <a href={url} target="_blank" rel="noreferrer" className="block relative">
                        <img src={url} alt=""
                          className="w-full h-40 object-cover"/>
                        {p.source === "walk" && (
                          <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[8px] tracking-widest"
                            data-testid={`all-photos-source-badge-${i}`}>Walk</span>
                        )}
                        {p.chip_note && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/70 to-transparent p-1.5 pt-4"
                            data-testid={`all-photos-chip-note-${i}`}>
                            {p.chip_label && (
                              <div className="text-[8px] font-black uppercase tracking-widest text-fuchsia-300 leading-tight flex items-center gap-1">
                                <span>✋ {p.chip_label}</span>
                                {p.chip_status && (
                                  <span className={`px-1 py-0.5 ${p.chip_status === "pass" ? "bg-emerald-500" : p.chip_status === "fail" ? "bg-red-500" : "bg-slate-500"} text-white`}>
                                    {p.chip_status}
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="text-[10px] text-white font-semibold leading-tight line-clamp-3">
                              {p.chip_note}
                            </div>
                            {p.overridden_by && (
                              <div className="text-[8px] text-slate-300 leading-tight mt-0.5">
                                — {p.overridden_by}
                              </div>
                            )}
                          </div>
                        )}
                      </a>
                      <div className="p-1.5">
                        {p.room && (
                          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 leading-tight">
                            {p.room}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-700 font-semibold leading-tight line-clamp-2">
                          {p.item_title || "(no title)"}
                        </div>
                        <div className="text-[9px] text-slate-400 leading-tight mt-0.5">
                          {p.by || "—"}{stamp ? ` · ${stamp}` : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Compare Lightbox */}
      {compareOpen && (() => {
        const token = jwt();
        const src = (p) => p ? `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(p.path)}&auth=${encodeURIComponent(token)}` : "";
        const mate = compareOpen.pair_id
          ? (proj.project_photos || []).find(x => x.id === compareOpen.pair_id)
          : null;
        const isBefore = compareOpen.kind === "before";
        const before = isBefore ? compareOpen : mate;
        const after = !isBefore ? compareOpen : mate;
        const oppositeKind = isBefore ? "after" : "before";
        const candidates = (proj.project_photos || []).filter(p => p.kind === oppositeKind && !p.pair_id);
        return (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="album-compare-modal"
          onClick={() => setCompareOpen(null)}>
          <div className="flex items-center justify-between p-3 text-white" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="font-display font-black uppercase tracking-tight text-lg">📸 Compare</div>
              {mate && (
                <>
                  <div className="flex border-2 border-white/40">
                    <button onClick={() => setCompareMode("split")} data-testid="compare-mode-split"
                      className={`h-8 px-2 font-black uppercase text-[10px] tracking-widest ${compareMode === "split" ? "bg-white text-black" : "text-white hover:bg-white/10"}`}>
                      ⬒ Split
                    </button>
                    <button onClick={() => { setCompareMode("slider"); setSliderPct(50); }} data-testid="compare-mode-slider"
                      className={`h-8 px-2 font-black uppercase text-[10px] tracking-widest ${compareMode === "slider" ? "bg-white text-black" : "text-white hover:bg-white/10"}`}>
                      ⇄ Slider
                    </button>
                  </div>
                  <button onClick={() => unpairPhoto(compareOpen.id)} disabled={pairBusy}
                    data-testid="album-compare-unpair"
                    className="h-8 px-2 border-2 border-white/40 text-white hover:bg-white/10 font-black uppercase text-[10px] tracking-widest">
                    Unpair
                  </button>
                </>
              )}
            </div>
            <button onClick={() => setCompareOpen(null)} data-testid="album-compare-close"
              className="h-9 w-9 border-2 border-white/40 text-white hover:bg-white/10 font-black">✕</button>
          </div>
          <div className="flex-1 overflow-auto p-3" onClick={e => e.stopPropagation()}>
            {mate ? (
              compareMode === "slider" ? (
                <div className="max-w-5xl mx-auto" data-testid="compare-slider-panel">
                  <div className="relative w-full max-h-[75vh] mx-auto select-none" style={{aspectRatio: "16/10"}}>
                    <img src={src(before)} alt="Before" draggable={false}
                      className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none"/>
                    <img src={src(after)} alt="After" draggable={false}
                      style={{clipPath: `inset(0 0 0 ${sliderPct}%)`}}
                      className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none"/>
                    <div className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(0,0,0,0.6)] pointer-events-none"
                      style={{left: `${sliderPct}%`, transform: "translateX(-50%)"}}>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white text-black flex items-center justify-center font-black shadow-lg">⇄</div>
                    </div>
                    <input type="range" min="0" max="100" value={sliderPct}
                      onChange={e => setSliderPct(parseInt(e.target.value))}
                      data-testid="compare-slider-range"
                      aria-label="Reveal After"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize touch-none"/>
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-orange-500 text-white font-black uppercase text-[10px] tracking-widest">Before</div>
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white font-black uppercase text-[10px] tracking-widest">After</div>
                  </div>
                  <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400 mt-3">
                    Drag the ⇄ bar left/right · touch or mouse
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-6xl mx-auto">
                  <figure className="bg-black" data-testid="compare-before-panel">
                    <div className="p-2 bg-orange-500 text-white font-black uppercase text-[10px] tracking-widest">Before · {before?.uploaded_by || ""}</div>
                    <img src={src(before)} alt="Before" className="w-full max-h-[70vh] object-contain bg-black"/>
                  </figure>
                  <figure className="bg-black" data-testid="compare-after-panel">
                    <div className="p-2 bg-emerald-500 text-white font-black uppercase text-[10px] tracking-widest">After · {after?.uploaded_by || ""}</div>
                    <img src={src(after)} alt="After" className="w-full max-h-[70vh] object-contain bg-black"/>
                  </figure>
                  <div className="md:col-span-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Tap ⇄ Slider above for the reveal effect
                  </div>
                </div>
              )
            ) : (
              <div className="max-w-4xl mx-auto space-y-3">
                <figure className="bg-black">
                  <div className={`p-2 text-white font-black uppercase text-[10px] tracking-widest ${isBefore ? "bg-orange-500" : "bg-emerald-500"}`}>
                    {compareOpen.kind} · {compareOpen.uploaded_by || ""}
                  </div>
                  <img src={src(compareOpen)} alt={compareOpen.kind} className="w-full max-h-[55vh] object-contain bg-black"/>
                </figure>
                <div className="p-3 bg-white border-2 border-fuchsia-500" data-testid="compare-pair-picker">
                  <div className="font-display font-black uppercase text-sm tracking-tight mb-2">
                    🔗 Pair with an <span className="text-fuchsia-700">{oppositeKind}</span> photo
                  </div>
                  {candidates.length === 0 ? (
                    <div className="p-3 text-center text-xs text-slate-500 font-medium border border-dashed border-slate-300">
                      No unpaired {oppositeKind} photos yet · upload one first
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                      {candidates.map(c => (
                        <button key={c.id} onClick={() => pairPhotos(isBefore ? compareOpen.id : c.id, isBefore ? c.id : compareOpen.id)}
                          disabled={pairBusy} data-testid={`compare-pick-${c.id}`}
                          className="relative aspect-square bg-slate-100 border-2 border-slate-200 hover:border-fuchsia-500 transition-colors disabled:opacity-40">
                          <img src={src(c)} alt="candidate" className="w-full h-full object-cover"/>
                          <div className="absolute inset-0 flex items-center justify-center bg-fuchsia-600/0 hover:bg-fuchsia-600/60 text-white opacity-0 hover:opacity-100 font-black uppercase text-[10px] tracking-widest">
                            Pair
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Founder-only Tradesman Magic Links (hidden from all other roles) */}
      {(user?.is_founder || user?.platform_role === "super_admin") && (
        <section data-testid="tradesman-links-section" className="bg-white border-2 border-[#0f172a] mb-3">
          <div className="p-2 flex items-center gap-2 border-b-2 border-slate-100 flex-wrap">
            <div className="font-display font-black text-sm uppercase tracking-tight">🛠️ Tradesman Links</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Photos + status only · pricing/docs stay hidden · optional PIN
            </div>
          </div>
          <div className="p-2 grid grid-cols-1 md:grid-cols-12 gap-2 border-b-2 border-slate-50">
            <input value={tradeLabel} onChange={e => setTradeLabel(e.target.value)}
              placeholder="Who is this for? (e.g. Cindy · LCV)"
              data-testid="trade-label-input"
              className="md:col-span-5 h-9 px-2 border-2 border-[#0f172a] text-sm"/>
            <select value={tradeChoice} onChange={e => setTradeChoice(e.target.value)}
              data-testid="trade-choice"
              className="md:col-span-3 h-9 px-2 border-2 border-[#0f172a] text-sm font-black uppercase tracking-widest">
              {TRADES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label className="md:col-span-2 h-9 px-2 border-2 border-[#0f172a] flex items-center gap-1 cursor-pointer bg-white hover:bg-slate-50" data-testid="trade-usepin-label">
              <input type="checkbox" checked={tradeUsePin} onChange={e => setTradeUsePin(e.target.checked)}
                data-testid="trade-usepin" className="h-4 w-4"/>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">🔒 PIN</span>
            </label>
            <button onClick={mintTradeLink} disabled={tradeBusy}
              data-testid="trade-mint-btn"
              className="md:col-span-2 h-9 px-3 bg-[#00C4CC] hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-40">
              {tradeBusy ? "…" : "+ Mint"}
            </button>
          </div>

          {freshPin && (
            <div className="p-3 bg-amber-50 border-b-4 border-amber-500" data-testid="fresh-pin-banner">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
                🔑 Save this PIN — you won't see it again
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-mono font-black text-3xl tracking-[0.4em] text-amber-900" data-testid="fresh-pin-code">
                  {freshPin.pin}
                </div>
                <div className="flex gap-1 flex-wrap ml-auto">
                  <button onClick={() => { navigator.clipboard.writeText(`Link: ${freshPin.url}\nPIN: ${freshPin.pin}`); toast.success("Link + PIN copied"); }}
                    data-testid="fresh-pin-copy"
                    className="h-8 px-2 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest">
                    📋 Copy Both
                  </button>
                  <a href={`sms:?body=${encodeURIComponent(`Hi ${freshPin.label} — your Field Radar link + PIN.\n\nLink: ${freshPin.url}\nPIN: ${freshPin.pin}\n\nOpen the link on your phone, type the PIN once, then upload photos + mark items complete. No app to install.`)}`}
                    data-testid="fresh-pin-sms"
                    className="h-8 px-2 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">
                    💬 Text Both
                  </a>
                  <button onClick={() => setFreshPin(null)}
                    className="h-8 px-2 bg-slate-200 text-slate-700 font-black uppercase text-[10px] tracking-widest">
                    ✕ Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {tradeLinks.length === 0 ? (
            <div className="p-3 text-xs text-slate-500 text-center italic">
              No tradesman links yet. Mint one, pick the trade, add a PIN if it's a third-party sub, and text it. No app download needed on their end.
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {tradeLinks.map(l => {
                const url = `${window.location.origin}/tradesman/${l.token}`;
                const revoked = l.revoked;
                const trade = TRADES.find(t => t[0] === l.trade)?.[1] || "👷 Field Crew";
                const smsBody = `Hi ${l.label || "there"} — here's your Field Radar ${trade.replace(/^[^ ]+ /, "").toLowerCase()} link for ${proj?.name || "the property"}: ${url}${l.has_pin ? "\n\n(PIN sent separately)" : ""}\n\nOpen on your phone, tap items to add photos, mark complete. No app to install.`;
                return (
                  <div key={l.token} className={`p-2 border-2 ${revoked ? "border-slate-300 bg-slate-50 opacity-60" : l.shared_warning ? "border-amber-500 bg-amber-50" : "border-[#0f172a] bg-white"}`}
                    data-testid={`trade-link-${l.token}`}>
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-black text-sm truncate">{l.label || "Tradesman"}</span>
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 border border-slate-300">{trade}</span>
                          {l.has_pin && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">🔒 PIN</span>}
                          {l.shared_warning && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-500 text-white">⚠️ Shared? {l.device_count} devices</span>}
                          {revoked && <span className="text-[10px] font-black uppercase tracking-widest text-red-600">Revoked</span>}
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate mt-0.5">{url}</div>
                        <div className="text-[9px] text-slate-500 mt-0.5">
                          📸 {l.photo_count || 0} photos · 🔄 {l.status_change_count || 0} status changes
                          {l.last_used_at && ` · last used ${l.last_used_at.slice(0,10)}`}
                        </div>
                      </div>
                      {!revoked && (
                        <div className="flex gap-1 flex-wrap">
                          <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }}
                            data-testid={`trade-copy-${l.token}`}
                            className="h-8 px-2 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest">📋 Copy</button>
                          <a href={`sms:?body=${encodeURIComponent(smsBody)}`}
                            data-testid={`trade-sms-${l.token}`}
                            className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">💬 Text</a>
                          <a href={url} target="_blank" rel="noreferrer"
                            data-testid={`trade-open-${l.token}`}
                            className="h-8 px-2 bg-slate-200 hover:bg-slate-300 text-slate-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center">↗ Preview</a>
                          <button onClick={() => revokeTradeLink(l.token)}
                            data-testid={`trade-revoke-${l.token}`}
                            className="h-8 px-2 bg-red-100 hover:bg-red-200 text-red-800 font-black uppercase text-[10px] tracking-widest">🚫 Revoke</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Founder-only Private Docs (hidden from all other roles) */}
      {(user?.is_founder || user?.platform_role === "super_admin") && (
        <section data-testid="project-docs-section" className="bg-white border-2 border-[#0f172a] mb-3">
          <div className="p-2 flex items-center gap-2 border-b-2 border-slate-100 flex-wrap">
            <div className="font-display font-black text-sm uppercase tracking-tight">{t("pr.docs.title")}</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t("pr.docs.sub")}</div>
            <a href={`${BASE}/api/projects/${id}/amended-quote.pdf?auth=${encodeURIComponent(jwt())}`}
              target="_blank" rel="noreferrer"
              data-testid="amended-quote-pdf-btn"
              className="ml-auto h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              📄 Amended Quote PDF
            </a>
            <label className="h-8 px-3 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest cursor-pointer inline-flex items-center gap-1" data-testid="doc-upload-btn">
              + Upload Doc
              <input type="file" className="hidden"
                onChange={async e => {
                  const f = e.target.files?.[0]; e.target.value = "";
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  fd.append("label", f.name);
                  try {
                    await axios.post(`${BASE}/api/projects/${id}/docs`, fd,
                      { ...auth(), headers: { ...(auth().headers || {}), "Content-Type": "multipart/form-data" }});
                    toast.success("Doc uploaded");
                    load();
                  } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Upload failed")); }
                }}/>
            </label>
          </div>
          <div className="p-2">
            {(proj.private_docs || []).length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400 font-black uppercase tracking-widest border border-dashed border-slate-200">
                No private docs yet · signed quotes, contracts, invoices land here
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(proj.private_docs || []).map(d => {
                  const token = jwt();
                  const src = `${BASE}/api/projects/${id}/punch-photo?path=${encodeURIComponent(d.path)}&auth=${encodeURIComponent(token)}`;
                  return (
                    <li key={d.id} className="py-2 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
                      <div className="text-2xl">{d.content_type?.includes("pdf") ? "📄" : "📎"}</div>
                      <div className="min-w-0 flex-1">
                        <a href={src} target="_blank" rel="noreferrer" className="font-black text-sm text-slate-800 hover:text-cyan-700 underline truncate block">{d.label || d.filename}</a>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by} · {(d.uploaded_at || "").slice(0, 10)}
                        </div>
                      </div>
                      <button onClick={async () => {
                        if (!window.confirm(`Delete "${d.label || d.filename}"?`)) return;
                        try {
                          await axios.delete(`${BASE}/api/projects/${id}/docs/${d.id}`, auth());
                          toast.success("Deleted");
                          load();
                        } catch { toast.error("Couldn't delete"); }
                      }} data-testid={`doc-delete-${d.id}`}
                        className="h-8 px-2 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest">✕</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* Sticky bulk-action bar */}
      {bulkMode && selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-[#0f172a] border-2 border-emerald-500 shadow-[6px_6px_0px_#10b981] p-2" data-testid="bulk-action-bar">
          <div className="px-2 flex items-center text-white font-black uppercase text-[10px] tracking-widest">
            {selected.size} selected
          </div>
          <button onClick={() => runBulk({ status: "complete" })} disabled={bulkBusy} data-testid="bulk-complete-btn"
            className="h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
            {bulkBusy ? "…" : "✓ Mark Complete"}
          </button>
          <button onClick={() => runBulk({ status: "in_progress" })} disabled={bulkBusy} data-testid="bulk-inprog-btn"
            className="h-10 px-3 bg-blue-500 hover:bg-blue-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
            → In Prog
          </button>
          <button onClick={() => runBulk({ priority: "urgent" })} disabled={bulkBusy} data-testid="bulk-urgent-btn"
            className="h-10 px-3 bg-pink-500 hover:bg-pink-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40 animate-pulse">
            🚨 Urgent
          </button>
          <button onClick={clearSelection}
            className="h-10 px-3 bg-transparent text-slate-400 hover:text-white font-black uppercase text-[10px] tracking-widest">
            Clear
          </button>
        </div>
      )}
    </div>
    {showAIAssess && (
      <AIAssessModal projectId={id} projectName={proj?.name}
        items={proj?.punch_items || []}
        onClose={() => setShowAIAssess(false)}
        onApplied={load}/>
    )}
    {showVoicePunch && proj && (
      <VoiceToPunchModal
        projectId={proj.id || proj._id}
        projectName={proj.name}
        rooms={proj.rooms || []}
        onClose={() => setShowVoicePunch(false)}
        onAdded={() => load()}
      />
    )}
    {showWalkMode && proj && (
      <WalkMode project={proj} onClose={() => setShowWalkMode(false)} onChanged={load}/>
    )}
    <AutoDeployCamModal
      open={camDeploy.open}
      address={camDeploy.address}
      unit={camDeploy.unit}
      sourceProjectId={id}
      onClose={() => setCamDeploy({ open: false, address: "", unit: "" })}
      onDeployed={() => setCamDeploy({ open: false, address: "", unit: "" })}
    />
    </AdminShell>
  );
}


// ── SCHEDULED VISITS PANEL ─────────────────────────────────────────
const VISIT_KIND_STYLE = {
  move_in:    { label: "Move-in",    bg: "bg-emerald-600 text-white" },
  move_out:   { label: "Move-out",   bg: "bg-amber-500 text-white" },
  inspection: { label: "Inspection", bg: "bg-blue-600 text-white" },
  repair:     { label: "Repair",     bg: "bg-red-600 text-white" },
  access:     { label: "Access",     bg: "bg-slate-700 text-white" },
  other:      { label: "Visit",      bg: "bg-slate-500 text-white" },
};

function ScheduledVisitsPanel({ projectId, visits, address, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: "", units: "", kind: "move_in", note: "" });
  const [busy, setBusy] = useState(false);
  const [openByUnit, setOpenByUnit] = useState({});

  useEffect(() => {
    if (!address) return;
    axios.get(`${BASE}/api/left-behind/summary`, { ...auth(), params: { address }})
      .then(r => setOpenByUnit(r.data?.by_unit || {}))
      .catch(() => setOpenByUnit({}));
  }, [address, (visits || []).length]);

  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const grouped = {};
  (visits || []).forEach(v => {
    if (!grouped[v.date]) grouped[v.date] = [];
    grouped[v.date].push(v);
  });
  const dates = Object.keys(grouped).sort();
  const upcoming = (visits || []).filter(v => (v.date || "") >= today).length;

  const submit = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    const tokens = (form.units || "")
      .split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (tokens.length === 0) { toast.error("Enter at least one unit (e.g. 1A, 3A, 4B)"); return; }
    setBusy(true);
    try {
      const body = { visits: tokens.map(unit => ({ unit, date: form.date, kind: form.kind, note: form.note })) };
      const { data } = await axios.post(`${BASE}/api/projects/${projectId}/scheduled-visits/bulk`, body, auth());
      onChange([...(visits || []), ...(data.visits || [])]);
      toast.success(`Added ${data.added} visit${data.added === 1 ? "" : "s"}`);
      setForm({ date: "", units: "", kind: "move_in", note: "" });
      setShowAdd(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't add visits"));
    } finally { setBusy(false); }
  };

  const removeVisit = async (id) => {
    if (!window.confirm("Remove this visit?")) return;
    try {
      await axios.delete(`${BASE}/api/projects/${projectId}/scheduled-visits/${id}`, auth());
      onChange((visits || []).filter(v => v.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const fmt = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    const dt = new Date(+y, +m - 1, +day);
    return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="mb-4 bg-white border-2 border-[#0f172a]" data-testid="scheduled-visits-panel">
      <div className="p-3 bg-[#0f172a] text-white flex items-center gap-2 flex-wrap">
        <CalendarBlank weight="fill" size={16}/>
        <div className="font-display font-black text-sm uppercase tracking-widest">Scheduled Visits</div>
        <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-500 text-slate-900 px-2 py-0.5">
          {upcoming} upcoming
        </span>
        <span className="text-[10px] opacity-70 ml-1">
          {(visits || []).length} total
        </span>
        <button onClick={() => setShowAdd(v => !v)} data-testid="visits-add-toggle"
          className="ml-auto h-8 px-3 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Plus weight="bold" size={12}/> {showAdd ? "Cancel" : "Add visits"}
        </button>
      </div>

      {showAdd && (
        <div className="p-3 border-b-2 border-slate-200 bg-emerald-50 space-y-2" data-testid="visits-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1">Date</div>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full h-9 px-2 border-2 border-slate-300 text-sm" data-testid="visits-date-input"/>
            </div>
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1">Kind</div>
              <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}
                className="w-full h-9 px-2 border-2 border-slate-300 text-sm" data-testid="visits-kind-select">
                {Object.entries(VISIT_KIND_STYLE).map(([k, s]) => (
                  <option key={k} value={k}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1">
                Units (comma / space / new-line separated)
              </div>
              <input value={form.units} onChange={e => setForm({ ...form, units: e.target.value })}
                placeholder="e.g. 1A, 3A, 4B, 11C"
                className="w-full h-9 px-2 border-2 border-slate-300 text-sm font-mono" data-testid="visits-units-input"/>
            </div>
          </div>
          <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
            placeholder="Optional note (e.g. 'college roommate #1')"
            className="w-full h-9 px-2 border-2 border-slate-300 text-sm" data-testid="visits-note-input"/>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] text-slate-500">
              Tip: repeat the same unit to schedule multiple visits same day (e.g. two roommates)
            </div>
            <button onClick={submit} disabled={busy} data-testid="visits-save-btn"
              className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
              {busy ? "Saving…" : "Pin dates"}
            </button>
          </div>
        </div>
      )}

      {dates.length === 0 && !showAdd && (
        <div className="p-6 text-center text-slate-500 text-sm">
          No visits scheduled yet. Hit <b>Add visits</b> to pin move-in / inspection dates per unit.
        </div>
      )}

      {dates.length > 0 && (
        <div className="p-3 space-y-3" data-testid="visits-list">
          {dates.map(date => (
            <div key={date} data-testid={`visits-day-${date}`}>
              <div className="flex items-baseline gap-2 mb-1">
                <div className={`font-display font-black text-lg ${date < today ? "text-slate-400" : "text-slate-900"}`}>
                  {fmt(date)}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {grouped[date].length} visit{grouped[date].length === 1 ? "" : "s"}
                  {date < today && <span className="ml-2 text-red-500">· past</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {grouped[date].map(v => {
                  const s = VISIT_KIND_STYLE[v.kind] || VISIT_KIND_STYLE.other;
                  const openItems = openByUnit[v.unit] || 0;
                  const pdfHref = `${BASE}/api/units/move-in-report.pdf?address=${encodeURIComponent(address || "")}&unit=${encodeURIComponent(v.unit)}&auth=${encodeURIComponent(jwt())}`;
                  return (
                    <div key={v.id} data-testid={`visit-chip-${v.id}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 ${s.bg} font-black text-xs`}>
                      <span className="font-mono">{v.unit}</span>
                      <span className="opacity-75 text-[9px] uppercase tracking-widest">{s.label}</span>
                      {openItems > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-black uppercase tracking-widest"
                          data-testid={`visit-warn-${v.id}`}
                          title={`${openItems} unresolved left-behind item${openItems === 1 ? "" : "s"} in ${v.unit}`}>
                          ⚠ {openItems} left
                        </span>
                      )}
                      {v.note && <span className="opacity-90 text-[10px] italic normal-case font-normal max-w-[160px] truncate">· {v.note}</span>}
                      <a href={pdfHref} target="_blank" rel="noopener noreferrer"
                        data-testid={`visit-pdf-${v.id}`}
                        title="Download Move-In Ready PDF"
                        className="ml-1 px-1 py-0.5 bg-white text-slate-900 hover:bg-emerald-100 inline-flex items-center text-[9px] font-black uppercase tracking-widest">
                        <FilePdf weight="fill" size={10}/>PDF
                      </a>
                      <button onClick={() => removeVisit(v.id)}
                        data-testid={`visit-del-${v.id}`}
                        className="ml-1 opacity-70 hover:opacity-100 text-[11px] leading-none">✕</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── LEFT-BEHIND ROUND-UP on the project page ────────────────────────
function LeftBehindRoundUp({ address }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const load = () => {
    if (!address) { setLoading(false); return; }
    axios.get(`${BASE}/api/left-behind`, { ...auth(), params: { address }})
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [address]);

  const visible = showResolved ? items : items.filter(i => i.status === "open");
  const byUnit = {};
  visible.forEach(it => {
    if (!byUnit[it.unit]) byUnit[it.unit] = [];
    byUnit[it.unit].push(it);
  });
  const units = Object.keys(byUnit).sort();
  const openCount = items.filter(i => i.status === "open").length;

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${BASE}/api/left-behind/${id}`, { status }, auth());
      load();
    } catch { toast.error("Update failed"); }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="mb-4 bg-white border-2 border-amber-500" data-testid="project-left-behind">
      <div className="p-3 bg-amber-500 text-slate-900 flex items-center gap-2 flex-wrap">
        <Package weight="fill" size={16}/>
        <div className="font-display font-black text-sm uppercase tracking-widest">Left-Behind Round-Up</div>
        <span className="text-[10px] font-black uppercase tracking-widest bg-red-600 text-white px-2 py-0.5">
          {openCount} open
        </span>
        <span className="text-[10px] opacity-70 ml-1">across {units.length} unit{units.length === 1 ? "" : "s"}</span>
        <button onClick={() => setShowResolved(v => !v)}
          data-testid="project-lb-toggle-resolved"
          className="ml-auto h-8 px-3 bg-white border-2 border-slate-900 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest">
          {showResolved ? "Hide resolved" : "Show resolved"}
        </button>
      </div>
      <div className="p-3 space-y-3" data-testid="project-lb-list">
        {units.map(u => (
          <div key={u}>
            <div className="flex items-baseline gap-2 mb-1">
              <div className="font-display font-black text-base">Unit {u}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {byUnit[u].filter(i => i.status === "open").length} open · {byUnit[u].length} total
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {byUnit[u].map(it => {
                const isOpen = it.status === "open";
                return (
                  <div key={it.id} data-testid={`project-lb-card-${it.id}`}
                    className={`p-2 border-2 flex items-start gap-2 ${isOpen ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50 opacity-70"}`}>
                    {it.photo_path ? (
                      <a href={`${BASE}/${it.photo_path}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${BASE}/${it.photo_path}`} alt="" className="h-14 w-14 object-cover border border-slate-400"/>
                      </a>
                    ) : (
                      <div className="h-14 w-14 bg-slate-200 border border-slate-400 flex items-center justify-center text-slate-400 text-[9px] text-center px-1 shrink-0">No photo</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold break-words">{it.description}</div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
                        {(it.created_at || "").slice(0, 10)}
                      </div>
                      <button onClick={() => setStatus(it.id, isOpen ? "resolved" : "open")}
                        data-testid={`project-lb-toggle-${it.id}`}
                        className={`mt-1 h-6 px-2 text-[9px] font-black uppercase tracking-widest ${isOpen ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white border-2 border-slate-400 text-slate-600 hover:border-slate-900"}`}>
                        {isOpen ? "Resolve" : "Reopen"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
